import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Send, Paperclip, StickyNote, Tag as TagIcon, Clock,
  CheckCircle, ArrowRightLeft, Hand, FileText, Zap, ChevronLeft,
  Mic, Square, Forward, X, Download, Trash2, Pencil, CornerUpLeft, Pin, PinOff,
  BellOff, Bell, Settings, Users, LogOut, RefreshCw, Smile, Bookmark, SpellCheck,
  ChevronDown, Copy, Bot, AlertTriangle, MessageCircle, UserSquare, Ban,
  MapPin, BarChart3
} from 'lucide-react';
import api from '../services/api';
import socketService from '../services/socketService';
import ConfirmModal from './ConfirmModal';
import TransferModal from './TransferModal';
import ContactPickerModal from './ContactPickerModal';
import { showToast } from '../utils/toast';
import { activityLevel, activityLabel, activityHeadline } from '../utils/activity';
import '../styles/ConversationDetail.css';

// Detecta URLs em texto e converte em <a> clicáveis. Mantém o resto como texto
// plano (React faz escape automático, então não há risco de XSS).
// Cobre: http(s)://…, www.…, domínios nus (foo.com, bdapvc.com.br, foo.com/path).
// O TLD precisa ter 2-24 letras → evita falso positivo em "e.g.", "5.50", IPs etc.
const URL_REGEX = /((?:https?:\/\/)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,24}(?::\d+)?(?:\/[^\s<>"'(){}\[\]]*)?)/gi;
function linkify(text) {
  if (!text) return text;
  const out = [];
  let last = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    // Skip se faz parte de email (foo@bar.com) — não queremos linkar bar.com sozinho
    const prevChar = m.index > 0 ? text[m.index - 1] : '';
    if (prevChar === '@') continue;
    if (m.index > last) out.push(text.slice(last, m.index));
    let url = m[0];
    // Pontuação no final (.,;:!?)]) costuma não fazer parte da URL
    let trailing = '';
    while (/[.,;:!?)\]]$/.test(url)) { trailing = url.slice(-1) + trailing; url = url.slice(0, -1); }
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    out.push(
      <a key={`l${m.index}`} href={href} target="_blank" rel="noopener noreferrer">{url}</a>
    );
    if (trailing) out.push(trailing);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function avatarColor(name = '') {
  const colors = [
    '#4F46E5', '#7C3AED', '#DB2777', '#DC2626',
    '#EA580C', '#D97706', '#16A34A', '#0891B2',
    '#0284C7', '#7C3AED',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// Catálogo de emojis organizado por categoria — usado no picker.
const EMOJI_CATEGORIES = [
  { id: 'smileys', label: '😀', name: 'Sorrisos', emojis: '😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 😉 😊 😇 🥰 😍 🤩 😘 😗 ☺️ 😚 😙 🥲 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔 🫡 🤐 🤨 😐 😑 😶 🫥 😏 😒 🙄 😬 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 🤯 🤠 🥳 🥸 😎 🤓 🧐 😕 😟 🙁 ☹️ 😮 😯 😲 😳 🥺 🥹 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 👿 💀 💩 🤡 👻 👽 🤖'.split(' ') },
  { id: 'gestures', label: '👍', name: 'Gestos', emojis: '👋 🤚 🖐️ ✋ 🖖 👌 🤌 🤏 ✌️ 🤞 🫰 🤟 🤘 🤙 🫵 🫱 🫲 🫸 🫷 🫳 🫴 👈 👉 👆 🖕 👇 ☝️ 👍 👎 ✊ 👊 🤛 🤜 👏 🙌 🫶 👐 🤲 🤝 🙏 ✍️ 💅 🤳 💪 🦾 🦵 🦿 🦶 👂 🦻 👃 🧠 🫀 🫁 🦷 🦴 👀 👁️ 👅 👄 🫦'.split(' ') },
  { id: 'hearts',   label: '❤️', name: 'Corações', emojis: '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 💟 ♥️ 💋 💌 💯 ✨ ⭐ 🌟 💫 🔥 💥 🌈 ☀️'.split(' ') },
  { id: 'animals',  label: '🐶', name: 'Animais', emojis: '🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐻‍❄️ 🐨 🐯 🦁 🐮 🐷 🐽 🐸 🐵 🙈 🙉 🙊 🐒 🐔 🐧 🐦 🐤 🦆 🦅 🦉 🦇 🐺 🐗 🐴 🦄 🐝 🪲 🐛 🦋 🐌 🐞 🐜 🐢 🐍 🦎 🐙 🐠 🐟 🐡 🐬 🦈 🐳 🐋 🐊 🐅 🐆 🦓 🦍 🦧 🐘 🦛 🦏 🐪 🐫 🦒 🦘 🐃 🐂 🐄 🐎 🐖 🐏 🐑 🐐 🦌 🐕 🐩 🐈 🐓 🦃 🦚 🦜 🦢 🦩 🐇 🦝 🦨 🦡 🦦 🦥 🐁 🐀 🐿️'.split(' ') },
  { id: 'food',     label: '🍎', name: 'Comida', emojis: '🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🫐 🍈 🍒 🍑 🥭 🍍 🥥 🥝 🍅 🍆 🥑 🥦 🥬 🥒 🌶️ 🫑 🌽 🥕 🫒 🧄 🧅 🥔 🍠 🥐 🥯 🍞 🥖 🥨 🧀 🥚 🍳 🧈 🥞 🧇 🥓 🥩 🍗 🍖 🦴 🌭 🍔 🍟 🍕 🥪 🌮 🌯 🫔 🥙 🧆 🥚 🍳 🥘 🍲 🫕 🥣 🥗 🍿 🧈 🧂 🥫 🍱 🍘 🍙 🍚 🍛 🍜 🍝 🍠 🍢 🍣 🍤 🍥 🥮 🍡 🥟 🥠 🥡 🦪 🍦 🍧 🍨 🍩 🍪 🎂 🍰 🧁 🥧 🍫 🍬 🍭 🍮 🍯 🍼 🥛 ☕ 🫖 🍵 🍶 🍾 🍷 🍸 🍹 🍺 🍻 🥂 🥃'.split(' ') },
  { id: 'activity', label: '⚽', name: 'Atividades', emojis: '⚽ 🏀 🏈 ⚾ 🥎 🎾 🏐 🏉 🥏 🎱 🪀 🏓 🏸 🏒 🏑 🥍 🏏 🪃 🥅 ⛳ 🪁 🏹 🎣 🤿 🥊 🥋 🎽 🛹 🛼 🛷 ⛸️ 🥌 🎿 ⛷️ 🏂 🪂 🏋️ 🤼 🤸 ⛹️ 🤺 🤾 🏌️ 🏇 🧘 🏄 🏊 🤽 🚣 🧗 🚵 🚴 🏆 🥇 🥈 🥉 🎖️ 🏅 🎫 🎟️ 🎪 🤹 🎭 🩰 🎨 🎬 🎤 🎧 🎼 🎹 🥁 🪘 🎷 🎺 🎸 🪕 🎻 🎲 ♟️ 🎯 🎳 🎮 🎰 🧩'.split(' ') },
  { id: 'travel',   label: '🚗', name: 'Lugares', emojis: '🚗 🚕 🚙 🚌 🚎 🏎️ 🚓 🚑 🚒 🚐 🛻 🚚 🚛 🚜 🦯 🦽 🦼 🛴 🚲 🛵 🏍️ 🛺 🚨 🚔 🚍 🚘 🚖 🚡 🚠 🚟 🚃 🚋 🚞 🚝 🚄 🚅 🚈 🚂 🚆 🚇 🚊 🚉 ✈️ 🛫 🛬 🛩️ 💺 🛰️ 🚀 🛸 🚁 🛶 ⛵ 🚤 🛥️ 🛳️ ⛴️ 🚢 ⚓ 🪝 ⛽ 🚧 🚦 🚥 🚏 🗺️ 🗿 🗽 🗼 🏰 🏯 🏟️ 🎡 🎢 🛝 🎠 ⛲ ⛱️ 🏖️ 🏝️ 🏜️ 🌋 ⛰️ 🏔️ 🗻 🏕️ ⛺ 🛖 🏠 🏡 🏘️ 🏚️ 🏗️ 🏭 🏢 🏬 🏣 🏤 🏥 🏦 🏨 🏪 🏫 🏩 💒 🏛️ ⛪ 🕌 🛕 🕍 🕋 ⛩️ 🛤️ 🛣️ 🗾 🎑 🏞️ 🌅 🌄 🌠 🎇 🎆 🌇 🌆 🏙️ 🌃 🌌 🌉 🌁'.split(' ') },
  { id: 'objects',  label: '💡', name: 'Objetos', emojis: '💡 🔦 🕯️ 🪔 🧯 🛢️ 💸 💵 💴 💶 💷 🪙 💰 💳 🪪 💎 ⚖️ 🪜 🧰 🔧 🪛 🔨 ⚒️ 🛠️ ⛏️ 🪚 🔩 ⚙️ 🪤 🧱 ⛓️ 🧲 🔫 💣 🧨 🪓 🔪 🗡️ ⚔️ 🛡️ 🚬 ⚰️ ⚱️ 🏺 🔮 📿 🧿 💈 ⚗️ 🔭 🔬 🕳️ 🩹 🩺 💊 💉 🩸 🧬 🦠 🧫 🧪 🌡️ 🧹 🪠 🧺 🧻 🚽 🚰 🚿 🛁 🛀 🧼 🪥 🪒 🧽 🪣 🧴 🛎️ 🔑 🗝️ 🚪 🪑 🛋️ 🛏️ 🛌 🧸 🪆 🖼️ 🪞 🪟 🛍️ 🛒 🎁 🎈 🎏 🎀 🪄 🪅 🎊 🎉 🎎 🏮 🎐 🧧 ✉️ 📩 📨 📧 💌 📥 📤 📦 🏷️ 🪧 📪 📫 📬 📭 📮 📯 📜 📃 📄 📑 🧾 📊 📈 📉 🗒️ 🗓️ 📆 📅 🗑️ 📇 🗃️ 🗳️ 🗄️ 📋 📁 📂 🗂️ 🗞️ 📰 📓 📔 📒 📕 📗 📘 📙 📚 📖 🔖 🧷 🔗 📎 🖇️ 📐 📏 🧮 📌 📍 ✂️ 🖊️ 🖋️ ✒️ 🖌️ 🖍️ 📝 ✏️ 🔍 🔎 🔏 🔐 🔒 🔓'.split(' ') },
  { id: 'symbols',  label: '✅', name: 'Símbolos', emojis: '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💯 ✅ ❎ ❌ 🚫 ⛔ 📵 🚭 🔞 ☢️ ☣️ ⚠️ 🚸 ✔️ ☑️ 🔘 🔴 🟠 🟡 🟢 🔵 🟣 ⚫ ⚪ 🟤 🟥 🟧 🟨 🟩 🟦 🟪 ⬛ ⬜ 🟫 🔶 🔷 🔸 🔹 🔺 🔻 💠 🔘 🔳 🔲 ♻️ ⚜️ 🔱 📛 🔰 ⭕ ✳️ ✴️ ❇️ ©️ ®️ ™️ #️⃣ *️⃣ 0️⃣ 1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣ 6️⃣ 7️⃣ 8️⃣ 9️⃣ 🔟'.split(' ') },
];

// Compara duas datas ignorando hora — true se forem dias civis diferentes
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

// Rótulo amigável pra divisor de data: "Hoje" / "Ontem" / "Quarta-feira" (últimos 7d) / data completa
function formatDateLabel(d) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  if (day.getTime() === today.getTime()) return 'Hoje';
  if (day.getTime() === yesterday.getTime()) return 'Ontem';

  const diffDays = Math.round((today - day) / 86400000);
  if (diffDays > 0 && diffDays < 7) {
    return d.toLocaleDateString('pt-BR', { weekday: 'long' }).replace(/^./, c => c.toUpperCase());
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

// Cores fortes e legíveis pra headers de participante (estilo WhatsApp).
// Mesmo nome → mesma cor sempre.
const PARTICIPANT_COLORS = [
  '#0EA5A4', '#2563EB', '#7C3AED', '#DB2777', '#E11D48',
  '#F59E0B', '#16A34A', '#0891B2', '#DC2626', '#8B5CF6',
  '#0F766E', '#9333EA'
];
function participantColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return PARTICIPANT_COLORS[Math.abs(h) % PARTICIPANT_COLORS.length];
}

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '#';
}

// Limites do upload — espelham o middleware multer do backend (100MB e a
// whitelist de extensões). Checar aqui evita subir 100MB pra receber erro.
const MAX_UPLOAD_MB = 100;
const ACCEPTED_EXT = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'svg',
  'mp4', 'mov', 'webm', '3gp', 'mkv',
  'ogg', 'mp3', 'm4a', 'wav', 'aac', 'flac',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'txt', 'csv', 'xml', 'json', 'html',
  'zip', 'rar', '7z', 'tar', 'gz',
  'apk'
]);

// Aceita pela extensão OU por ser image/video/audio (mesma regra permissiva do
// backend, que libera essas categorias mesmo fora da whitelist).
function isAcceptedFile(file) {
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  if (ACCEPTED_EXT.has(ext)) return true;
  const mime = (file.type || '').split(';')[0];
  return mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/');
}

// Contato compartilhado: a API REST devolve contact_payload como string JSON,
// o socket já manda o array pronto. Aceita os dois e nunca quebra o render.
function parseContacts(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  try {
    const parsed = JSON.parse(payload);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// contact_payload guarda conteúdo estruturado de vários tipos (localização,
// enquete). Mesma dupla origem do parseContacts: string JSON via REST, objeto
// já pronto via socket.
function parsePayload(payload) {
  if (!payload) return null;
  if (typeof payload === 'object') return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function ConversationDetail({ conversation, onBack, onStartConversation }) {
  const [messages, setMessages] = useState([]);
  // Número do contato compartilhado que está sendo aberto (trava o botão)
  const [startingContact, setStartingContact] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  // Status "otimista" do atendimento. A prop `conversation` vem do Dashboard e
  // não é reescrita quando a conversa é assumida (nem ao clicar Aceitar, nem ao
  // responder uma da fila) — sem isso a barra de ações continuaria oferecendo
  // "Aceitar" numa conversa que já é minha. Resetado ao trocar de conversa.
  const [statusOverride, setStatusOverride] = useState(null);
  // Atalho "ir pro fim" — aparece só quando o atendente rolou pra cima
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Arrastar e soltar arquivo no chat
  const [dragActive, setDragActive] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  const [showNotes, setShowNotes] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledText, setScheduledText] = useState('');
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickMenu, setShowQuickMenu] = useState(false);

  const [reassigned, setReassigned] = useState(null);
  const [confirmResolve, setConfirmResolve] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  // Só pra re-renderizar o "ativo há X" do cabeçalho a cada minuto. Sem isto o
  // texto congela em "ativo há 2min" enquanto o atendente fica com o chat aberto.
  const [, setActivityTick] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showQuickPanel, setShowQuickPanel] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [audioPreview, setAudioPreview] = useState(null); // { url, file, sizeMB }
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [nameOverride, setNameOverride] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState(new Set());
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(null); // { id, text }
  const [msgMenu, setMsgMenu] = useState(null); // { msg, x, y }
  const [uploadProgress, setUploadProgress] = useState(null); // { name, percent, sizeMB }
  const [replyingTo, setReplyingTo] = useState(null); // { id, text, senderName, senderType }
  const [forwardConvs, setForwardConvs] = useState([]);
  const [forwardSearch, setForwardSearch] = useState('');
  const [forwardLoading, setForwardLoading] = useState(false);
  const [isPinned, setIsPinned] = useState(!!conversation?.is_pinned);
  const [isMuted, setIsMuted] = useState(!!conversation?.is_muted);
  // Anti-loop bot-a-bot (2026-06-16)
  const [isBot, setIsBot] = useState(!!conversation?.is_bot);
  const [autoReplyBlocked, setAutoReplyBlocked] = useState(!!conversation?.auto_reply_blocked);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [showStickerPanel, setShowStickerPanel] = useState(false);
  const [pickerTab, setPickerTab] = useState('emoji'); // 'emoji' | 'sticker'
  const [emojiCategory, setEmojiCategory] = useState('smileys');
  const [stickers, setStickers] = useState([]);
  const stickerInputRef = useRef(null);
  const [stickerUpload, setStickerUpload] = useState(null); // { name, percent, processing }
  const [spellcheck, setSpellcheck] = useState(null); // { matches, checking }
  const [autoSpellcheck, setAutoSpellcheck] = useState(
    () => localStorage.getItem('auto_spellcheck') !== 'false' // default: ligado
  );
  const toggleAutoSpellcheck = () => {
    setAutoSpellcheck(v => {
      const next = !v;
      localStorage.setItem('auto_spellcheck', String(next));
      try { showToast(next ? 'Correção automática ligada' : 'Correção automática desligada', 'info'); } catch {}
      return next;
    });
  };

  // Aplica auto-correção (1ª sugestão de cada match) e retorna o texto corrigido.
  // Falha em silêncio (rate-limit, timeout, etc.) e devolve o texto original.
  // Calcula a correção SEM efeito colateral — não mostra toast, não altera state.
  // Precisa ser puro porque roda em background enquanto o atendente digita (ver
  // spellCacheRef): avisar "Corrigido: x → y" no meio da digitação, antes de ele
  // ter enviado qualquer coisa, seria ruído. O toast fica com quem usa o
  // resultado, no envio.
  const computeCorrection = async (text) => {
    const nada = { corrected: text, applied: [] };
    if (!text || !text.trim()) return nada;
    try {
      const r = await api.post('/spellcheck/check', { text, language: 'pt-BR' }, { timeout: 6000 });
      const matches = r.data?.matches || [];
      if (!matches.length) return nada;
      // Aplica sugestões de TRÁS PRA FRENTE pra não bagunçar offsets
      let out = text;
      const applied = [];
      const sorted = [...matches].sort((a, b) => b.offset - a.offset);
      for (const m of sorted) {
        const replacement = m.replacements && m.replacements[0];
        if (!replacement) continue;
        const original = out.slice(m.offset, m.offset + m.length);
        if (original === replacement) continue;
        // Proteção a nome próprio (2026-06-16): nunca auto-troca palavra com
        // inicial maiúscula (nome de pessoa, empresa, lugar). O backend já
        // filtra a maioria; este guard é a rede final pra auto-correção nunca
        // mandar "João" virar outro nome sem o atendente ver. Typo em minúscula
        // continua sendo corrigido. Quem quiser ajustar nome usa o modal manual.
        if (/^[A-ZÀ-Ý]/.test(original)) continue;
        out = out.slice(0, m.offset) + replacement + out.slice(m.offset + m.length);
        applied.push(`${original} → ${replacement}`);
      }
      return { corrected: out, applied };
    } catch (e) {
      // Falha do corretor nunca bloqueia nem altera o texto — devolve o original.
      return nada;
    }
  };

  // Aplica a correção e avisa o atendente do que mudou. Só chamado no envio.
  const announceCorrection = ({ corrected, applied }) => {
    if (applied && applied.length) {
      const sample = applied.slice(0, 3).join(', ') + (applied.length > 3 ? '…' : '');
      showToast(`Corrigido: ${sample}`, 'info');
    }
    return corrected;
  };
  const [groupNewName, setGroupNewName] = useState('');
  const [groupDeptId, setGroupDeptId] = useState('');
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  const longPressRef = useRef(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // ── Paginação do histórico (2026-09-25) ──
  // Abrir uma conversa carregava TODAS as mensagens (a maior da base tem 1.070,
  // ~580 KB) e ainda repetia isso a cada 45s no sync de consistência. Agora abre
  // com a página mais recente e o resto vem por botão.
  const MESSAGES_PAGE = 100;
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [oldestMessageId, setOldestMessageId] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Guarda a métrica da rolagem ANTES de inserir mensagens antigas no topo, pra
  // devolver o atendente exatamente ao ponto em que estava lendo. Sem isso, o
  // conteúdo novo empurra a lista e a tela "pula".
  const prependAnchorRef = useRef(null);
  // Inserir no topo não pode acionar a auto-rolagem que segue mensagem nova.
  const skipAutoScrollRef = useRef(false);
  // "Pino no fim": verdadeiro enquanto o atendente está acompanhando o fim da
  // conversa. Existe porque descer ao fim UMA vez, na hora que as mensagens
  // chegam, não basta — imagens, PDFs e áudios só ganham altura DEPOIS de
  // carregar, e aí o conteúdo cresce e empurra a visão pra cima. O resultado é
  // abrir um chat com anexos e cair no meio do histórico (2026-09-28).
  const pinBottomRef = useRef(true);
  // Até quando ignorar eventos de rolagem por serem consequência de uma rolagem
  // que nós mesmos disparamos (ver handleMessagesScroll).
  const rolagemProgramadaAte = useRef(0);
  // Força a descida ao fim ignorando a posição atual — usado ao abrir/trocar
  // de conversa, onde o esperado é sempre cair na última mensagem.
  const forceScrollRef = useRef(true);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);
  // Correção ortográfica pré-calculada enquanto o atendente digita.
  //
  // Why-incidente 2026-09-25: handleSendMessage fazia `await autoCorrectText()`
  // ANTES do POST, com a caixa de texto já limpa. O LanguageTool leva ~500-600ms
  // no servidor (medido), mais a viagem até o navegador do atendente — então a
  // mensagem sumia do input e não aparecia na conversa por 1 a 2 segundos. Era
  // exatamente o "delay ao mandar mensagem" relatado. A correção sai do caminho
  // crítico: roda em background após uma pausa na digitação, e o envio só
  // aproveita o resultado se ele já estiver pronto para AQUELE texto exato.
  const spellCacheRef = useRef({ source: null, corrected: null, applied: [] });

  // Abaixo disto não vale corrigir: "ok", "sim", "certo" não têm o que consertar
  // e são justamente as respostas que o atendente dispara mais rápido — pagar
  // qualquer espera nelas é o pior dos mundos.
  const MIN_SPELL_LEN = 4;

  // Pré-calcula a correção depois de uma pausa na digitação. O resultado fica
  // pronto antes de o atendente clicar em enviar, então o envio não espera nada.
  // O cache é indexado pelo texto exato: se ele continuar digitando, a entrada
  // antiga simplesmente não casa no envio — na pior hipótese há um "miss", nunca
  // uma correção aplicada ao texto errado.
  useEffect(() => {
    if (!autoSpellcheck) return;
    const texto = newMessage.trim();
    if (texto.length < MIN_SPELL_LEN) return;
    if (spellCacheRef.current.source === texto) return;
    const t = setTimeout(async () => {
      const res = await computeCorrection(texto);
      spellCacheRef.current = { source: texto, corrected: res.corrected, applied: res.applied };
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMessage, autoSpellcheck]);
  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  const autoGrowTextarea = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  };
  const pendingMsgIds = useRef(new Set());
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    setNameOverride(null);
    setIsPinned(!!conversation?.is_pinned);
    setIsMuted(!!conversation?.is_muted);
    setIsBot(!!conversation?.is_bot);
    setAutoReplyBlocked(!!conversation?.auto_reply_blocked);
    setShowGroupPanel(false);
    setStatusOverride(null);
    setGroupNewName(conversation?.contact_name || '');
    setGroupDeptId(conversation?.department_id ? String(conversation.department_id) : '');
    setParticipants([]);

    // Conversa atribuída a outro atendente → modo leitura (vale para admin/supervisor
    // que enxerga todas, e também para qualquer caso de atribuição cruzada).
    // Pending (Aguardando) não tem dono — qualquer um pode aceitar.
    const otherAgent =
      conversation?.assigned_agent_id &&
      String(conversation.assigned_agent_id) !== String(currentUser.id) &&
      conversation.status !== 'pending';
    setReassigned(otherAgent ? { agentId: conversation.assigned_agent_id } : null);
  }, [conversation?.id, conversation?.is_pinned, conversation?.is_muted, conversation?.is_bot, conversation?.auto_reply_blocked, conversation?.assigned_agent_id, conversation?.status, currentUser.id]);

  // Status efetivo do atendimento: o override local ganha da prop enquanto o
  // Dashboard não recarrega a conversa selecionada.
  const convStatus = statusOverride || conversation?.status;

  // ── Group admin handlers ──
  const isAdminUser = ['admin', 'supervisor'].includes(currentUser.role);

  const fetchParticipants = async (force = false) => {
    if (!conversation?.is_group) return;
    setLoadingParticipants(true);
    try {
      const r = await api.get(`/conversations/${conversation.id}/participants${force ? '?force=1' : ''}`);
      setParticipants(r.data?.participants || []);
    } catch (e) {
      showToast('Falha ao carregar participantes', 'error');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleRenameGroup = async () => {
    const name = groupNewName.trim();
    if (!name) return showToast('Informe um nome', 'error');
    try {
      await api.put(`/conversations/${conversation.id}/group/subject`, { subject: name });
      showToast('Nome do grupo atualizado', 'success');
      setGroupNewName('');
    } catch (e) {
      showToast(e.response?.data?.message || 'Falha ao renomear', 'error');
    }
  };

  const handleSetDepartment = async () => {
    try {
      await api.put(`/conversations/${conversation.id}/department`, {
        departmentId: groupDeptId === '' ? null : Number(groupDeptId)
      });
      showToast('Setor atualizado', 'success');
    } catch (e) {
      showToast(e.response?.data?.message || 'Falha', 'error');
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Sair deste grupo? O número conectado será removido como participante.')) return;
    try {
      await api.delete(`/conversations/${conversation.id}/group/leave`);
      showToast('Saiu do grupo', 'success');
      setShowGroupPanel(false);
    } catch (e) {
      showToast(e.response?.data?.message || 'Falha ao sair do grupo', 'error');
    }
  };

  // ── Corretor ortográfico/gramatical (LanguageTool via backend) ──
  const handleSpellcheck = async () => {
    const text = newMessage.trim();
    if (!text) {
      showToast('Nada pra verificar — digite um texto primeiro', 'info');
      return;
    }
    setSpellcheck({ matches: [], checking: true, text });
    try {
      const r = await api.post('/spellcheck/check', { text, language: 'pt-BR' });
      const matches = r.data?.matches || [];
      if (!matches.length) {
        setSpellcheck(null);
        showToast('Nada a corrigir — tudo certo!', 'success');
        return;
      }
      setSpellcheck({ matches, checking: false, text });
    } catch (err) {
      setSpellcheck(null);
      showToast(err.response?.data?.message || 'Falha ao verificar', 'error');
    }
  };

  // Aplica a sugestão em uma posição específica do texto. Como o offset
  // muda quando substituímos, recalculamos os offsets dos matches restantes.
  const applySuggestion = (matchIdx, replacement) => {
    if (!spellcheck) return;
    const { matches, text } = spellcheck;
    const m = matches[matchIdx];
    if (!m) return;
    const before = text.slice(0, m.offset);
    const after = text.slice(m.offset + m.length);
    const newText = before + replacement + after;
    const delta = replacement.length - m.length;
    // Atualiza offsets dos demais matches que vêm DEPOIS deste
    const newMatches = matches
      .filter((_, i) => i !== matchIdx)
      .map(mm => mm.offset > m.offset ? { ...mm, offset: mm.offset + delta } : mm);
    setNewMessage(newText);
    if (newMatches.length === 0) {
      setSpellcheck(null);
      showToast('Texto corrigido!', 'success');
    } else {
      setSpellcheck({ matches: newMatches, checking: false, text: newText });
    }
  };

  const ignoreSuggestion = (matchIdx) => {
    if (!spellcheck) return;
    const next = spellcheck.matches.filter((_, i) => i !== matchIdx);
    if (!next.length) setSpellcheck(null);
    else setSpellcheck({ ...spellcheck, matches: next });
  };

  // ── Emojis ──
  const insertEmoji = (emoji) => {
    const ta = textareaRef.current;
    if (!ta) {
      setNewMessage(prev => prev + emoji);
      return;
    }
    const start = ta.selectionStart ?? newMessage.length;
    const end = ta.selectionEnd ?? newMessage.length;
    const next = newMessage.slice(0, start) + emoji + newMessage.slice(end);
    setNewMessage(next);
    setTimeout(() => {
      ta.focus();
      const pos = start + emoji.length;
      try { ta.setSelectionRange(pos, pos); } catch {}
      autoGrowTextarea(ta);
    }, 0);
  };

  // ── Stickers ──
  const fetchStickers = async () => {
    try { setStickers((await api.get('/stickers')).data || []); } catch (e) {}
  };

  const handleSendSticker = async (stickerId) => {
    try {
      await api.post(`/stickers/${stickerId}/send`, { conversationId: conversation.id });
      setShowStickerPanel(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Falha ao enviar figurinha', 'error');
    }
  };

  const handleUploadSticker = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setStickerUpload({ name: file.name, percent: 0, processing: false });
    try {
      await api.post('/stickers/upload', fd, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const percent = Math.round((evt.loaded / evt.total) * 100);
          setStickerUpload(prev => prev ? { ...prev, percent, processing: percent >= 100 } : prev);
        }
      });
      fetchStickers();
      showToast('Figurinha adicionada', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Falha no upload', 'error');
    } finally {
      setStickerUpload(null);
      e.target.value = '';
    }
  };

  const handleDeleteSticker = async (id, ev) => {
    ev.stopPropagation();
    if (!window.confirm('Remover esta figurinha da biblioteca?')) return;
    try {
      await api.delete(`/stickers/${id}`);
      setStickers(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      showToast('Falha ao remover', 'error');
    }
  };

  const handleSaveSticker = async (messageId) => {
    try {
      await api.post('/stickers/save-from-message', { messageId });
      showToast('Figurinha salva na biblioteca', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Falha ao salvar', 'error');
    }
  };

  const handleToggleMute = async () => {
    const next = !isMuted;
    setIsMuted(next);
    try {
      if (next) {
        await api.put(`/conversations/${conversation.id}/mute`);
        showToast('Notificações silenciadas', 'info');
      } else {
        await api.delete(`/conversations/${conversation.id}/mute`);
        showToast('Notificações ativadas', 'success');
      }
    } catch (err) {
      setIsMuted(!next);
      showToast(err.response?.data?.message || 'Erro ao alterar', 'error');
    }
  };

  const handleTogglePin = async () => {
    const next = !isPinned;
    setIsPinned(next); // otimista
    try {
      if (next) {
        await api.put(`/conversations/${conversation.id}/pin`);
        showToast('Conversa fixada', 'success');
      } else {
        await api.delete(`/conversations/${conversation.id}/pin`);
        showToast('Conversa desafixada', 'info');
      }
    } catch (err) {
      setIsPinned(!next); // reverte
      showToast(err.response?.data?.message || 'Erro ao alterar fixação', 'error');
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightboxUrl(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Soltar arquivo FORA da área do chat faz o navegador abrir o arquivo na aba
  // (perdendo a tela do atendimento). Cancelamos o default na janela toda; o
  // drop dentro do chat é tratado por handleDrop, que roda antes.
  useEffect(() => {
    const swallow = (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
    };
    const onWindowDrop = (e) => { swallow(e); clearDrag(); };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', onWindowDrop);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', onWindowDrop);
    };
  }, []);

  // Dados GLOBAIS — não mudam de uma conversa pra outra. Ficavam no efeito de
  // troca de conversa, então cada clique num chat disparava 5 requisições
  // inúteis (setores, usuários, todas as tags, respostas rápidas, quem está
  // online) que competiam com a busca das mensagens e travavam a transição.
  // (2026-09-28)
  useEffect(() => {
    fetchDepartments();
    fetchUsers();
    fetchAllTags();
    fetchQuickReplies();
    fetchOnlineUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!conversation) return;
    // Esvazia ANTES de buscar: sem isto, as mensagens da conversa anterior
    // continuavam montadas até a nova lista chegar, e aí o React tinha que
    // reconciliar 100 itens antigos com 100 novos de uma vez — é isso que
    // travava. Esvaziando, o desmonte é barato, o esqueleto aparece na hora
    // (`loadingMessages && messages.length === 0`) e a troca fica instantânea
    // aos olhos de quem usa.
    setMessages([]);
    setHasMoreMessages(false);
    setOldestMessageId(null);
    fetchMessages();
    fetchNotes();
    fetchConversationTags();

    const onNew = (data) => {
      if (data.conversationId === conversation.id) {
        // Se este id já foi adicionado via update optimista, ignorar o evento do socket
        if (pendingMsgIds.current.has(data.id)) {
          pendingMsgIds.current.delete(data.id);
          return;
        }
        setMessages(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          return [...prev, {
            id: data.id,
            message_text: data.messageText,
            sender_type: data.senderType,
            // Why-incidente 2026-09-23: sender_id NÃO era mapeado aqui. O socket
            // manda `senderId`, mas sem esta linha toda mensagem que chegava por
            // socket ficava com sender_id undefined — e como isMsgEditable compara
            // String(m.sender_id) com o id do usuário, a opção "Editar" sumia
            // justamente na mensagem recém-enviada, que é quando alguém quer
            // editar. Só reaparecia após refetch da API REST (recarregar a página),
            // quando a janela de 15min do WhatsApp já tinha passado.
            // Bot/agendador/webhook não mandam senderId — viram null, sem dono.
            sender_id: data.senderId ?? null,
            sender_name: data.senderName,
            media_url: data.mediaUrl,
            media_type: data.mediaType,
            file_name: data.fileName,
            is_system: !!data.isSystem,
            is_sticker: !!data.isSticker,
            participant_name: data.participantName || null,
            // Contato compartilhado: o socket manda o array já desserializado;
            // a API REST manda string JSON. parseContacts() aceita os dois.
            contact_payload: data.contactPayload || null,
            // Citação — os QUATRO campos andam juntos de propósito. O render usa
            // quoted_sender_type pra decidir a autoria; mapear só reply_to_id e
            // quoted_text faria a citação de uma mensagem da própria equipe cair
            // no fallback e aparecer como se fosse fala do cliente.
            reply_to_id: data.replyToId ?? null,
            quoted_text: data.quotedText ?? null,
            quoted_sender_type: data.quotedSenderType ?? null,
            quoted_sender_name: data.quotedSenderName ?? null,
            created_at: data.createdAt
          }];
        });
        if (data.senderType === 'client' && document.visibilityState === 'visible') {
          api.put(`/conversations/${conversation.id}/read`).catch(() => {});
        }
      }
    };

    const onAssigned = (data) => {
      if (data.conversationId !== conversation.id) return;
      if (data.agentId && String(data.agentId) !== String(currentUser.id)) {
        setReassigned({ agentId: data.agentId });
      } else if (data.agentId && data.status) {
        // Virou minha (cliquei em Aceitar ou respondi uma da fila) — a barra de
        // ações precisa parar de oferecer "Aceitar".
        setStatusOverride(data.status);
        setReassigned(null);
      }
    };

    const onPresence = (data) => setOnlineUserIds((data.onlineUserIds || []).map(String));

    const onMsgStatus = ({ messageId, status }) => {
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, msg_status: status } : m));
    };

    // Re-fetch messages after reconnect so nothing is missed during a brief disconnect
    const onReconnect = () => fetchMessages();

    const onContactUpdated = (data) => {
      if (data.phoneNumber && data.phoneNumber === conversation.contact_number) {
        setNameOverride(data.name);
      }
    };

    const onMessagesDeleted = (data) => {
      // Apagada "para todos" (revokedIds) não sai da lista: vira o placeholder
      // "Mensagem apagada", como no WhatsApp. Apagada só pra nós sai.
      const revoked = new Set(Array.isArray(data.revokedIds) ? data.revokedIds : []);
      const hidden = new Set(Array.isArray(data.messageIds) ? data.messageIds : []);
      if (!revoked.size && !hidden.size) return;
      setMessages(prev => prev
        .filter(m => !hidden.has(m.id))
        .map(m => revoked.has(m.id)
          ? { ...m, deleted_for_everyone: 1, message_text: null, media_url: null, contact_payload: null }
          : m
        )
      );
    };

    // Contato reagiu (ou removeu a reação) numa mensagem
    const onReaction = (data) => {
      if (data.conversationId !== conversation.id) return;
      setMessages(prev => prev.map(m =>
        m.id === data.messageId ? { ...m, reactions: data.reactions } : m
      ));
    };

    const onMessageEdited = (data) => {
      setMessages(prev => prev.map(m =>
        m.id === data.messageId
          ? { ...m, message_text: data.messageText, edited_at: data.editedAt }
          : m
      ));
    };

    // Anti-loop bot-a-bot: o detector travou a automação desta conversa.
    const onAutoReplyBlocked = (data) => {
      if (data.conversationId === conversation.id) setAutoReplyBlocked(true);
    };
    // conversation:updated carrega isBot/autoReplyBlocked após marcar/retomar
    // (inclusive disparado por outro atendente na mesma conversa).
    const onConvUpdated = (data) => {
      if (data.conversationId !== conversation.id) return;
      if (typeof data.isBot === 'boolean') setIsBot(data.isBot);
      if (data.autoReplyBlocked === false) setAutoReplyBlocked(false);
    };

    socketService.on('message:new', onNew);
    socketService.on('conversation:autoReplyBlocked', onAutoReplyBlocked);
    socketService.on('conversation:updated', onConvUpdated);
    socketService.on('conversation:assigned', onAssigned);
    socketService.on('presence:update', onPresence);
    socketService.on('message:status', onMsgStatus);
    socketService.on('contact:updated', onContactUpdated);
    socketService.on('messages:deleted', onMessagesDeleted);
    socketService.on('message:edited', onMessageEdited);
    socketService.on('message:reaction', onReaction);
    socketService.on('connect', onReconnect);

    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMessages();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchMessages);
    window.addEventListener('pageshow', fetchMessages);
    window.addEventListener('online', fetchMessages);

    // Checagem periódica de consistência (ver syncMessagesSilently)
    const syncTimer = setInterval(syncMessagesSilently, 45000);
    // Mantém o "ativo há X" do cabeçalho andando enquanto o chat fica aberto.
    const activityTimer = setInterval(() => setActivityTick(v => v + 1), 60000);

    return () => {
      clearInterval(syncTimer);
      clearInterval(activityTimer);
      socketService.off('message:new', onNew);
      socketService.off('conversation:autoReplyBlocked', onAutoReplyBlocked);
      socketService.off('conversation:updated', onConvUpdated);
      socketService.off('conversation:assigned', onAssigned);
      socketService.off('presence:update', onPresence);
      socketService.off('message:status', onMsgStatus);
      socketService.off('contact:updated', onContactUpdated);
      socketService.off('messages:deleted', onMessagesDeleted);
      socketService.off('message:edited', onMessageEdited);
      socketService.off('message:reaction', onReaction);
      socketService.off('connect', onReconnect);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchMessages);
      window.removeEventListener('pageshow', fetchMessages);
      window.removeEventListener('online', fetchMessages);
    };
  }, [conversation?.id]);

  // Distância do fim (px) a partir da qual consideramos que o atendente saiu
  // do "acompanhando ao vivo" — mostra o atalho e para de arrastar a tela.
  const SCROLL_BOTTOM_GAP = 240;

  const distanceFromBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return 0;
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    const el = messagesContainerRef.current;
    if (el) {
      // scrollTop direto em vez de scrollIntoView: não depende do elemento
      // âncora já estar no DOM nem de qual é o ancestral rolável mais próximo,
      // e não mexe na rolagem da página quando o container ainda não tem altura.
      el.scrollTo({ top: el.scrollHeight, behavior });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
    pinBottomRef.current = true;
    rolagemProgramadaAte.current = Date.now() + 600;
    setShowScrollDown(false);
  };

  const handleMessagesScroll = () => {
    // Rolagem suave dispara dezenas de eventos no meio do caminho, todos ainda
    // "longe do fim". Sem esta janela, eles soltariam o pino no meio da animação
    // e fariam o atalho de descer piscar. Só rolagem do próprio atendente conta.
    if (Date.now() < rolagemProgramadaAte.current) return;
    const longe = distanceFromBottom() > SCROLL_BOTTOM_GAP;
    setShowScrollDown(longe);
    // Rolou pra cima pra ler histórico → solta o pino (para de arrastar a tela).
    // Voltou pro fim → prende de novo.
    pinBottomRef.current = !longe;
  };

  // Troca de conversa: a próxima renderização de mensagens desce até o fim.
  useEffect(() => {
    forceScrollRef.current = true;
    pinBottomRef.current = true;
    setShowScrollDown(false);
  }, [conversation?.id]);

  // Mídia carregando é o que tirava o atendente do fim da conversa. Cada imagem,
  // vídeo ou PDF entra no DOM com altura ~0 e só empurra o conteúdo quando
  // termina de carregar — depois de a rolagem já ter ido pro fim. Enquanto o
  // pino estiver ativo, cada carregamento reancora no fim.
  //
  // O evento 'load' não borbulha, por isso o listener é registrado na fase de
  // CAPTURA (terceiro argumento true) — assim pega o load de qualquer <img>,
  // <video> ou <audio> lá dentro sem precisar de listener por elemento.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const reancorar = () => {
      if (pinBottomRef.current) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener('load', reancorar, true);
    el.addEventListener('loadedmetadata', reancorar, true);
    return () => {
      el.removeEventListener('load', reancorar, true);
      el.removeEventListener('loadedmetadata', reancorar, true);
    };
  }, [conversation?.id]);

  useEffect(() => {
    // Carregou histórico antigo no topo: a posição já foi restaurada no
    // useLayoutEffect. Descer ao fim aqui jogaria fora justamente o que ele
    // pediu pra ler.
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return;
    }
    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      scrollToBottom('auto');
      // Segunda ancoragem no quadro seguinte: cobre o reflow que acontece logo
      // após a primeira pintura (fontes, emoji, bolhas que só assumem a altura
      // final depois de medidas). Barato e elimina o "quase no fim".
      requestAnimationFrame(() => {
        const el = messagesContainerRef.current;
        if (el && pinBottomRef.current) el.scrollTop = el.scrollHeight;
      });
      return;
    }
    // Se o atendente rolou pra cima pra ler o histórico, mensagem nova NÃO
    // arrasta a tela — o atalho no canto avisa que tem coisa nova embaixo.
    // Exceção: mensagem enviada por ele mesmo, aí desce sempre.
    const last = messages[messages.length - 1];
    if (last?.sender_type === 'agent' || distanceFromBottom() <= SCROLL_BOTTOM_GAP) {
      scrollToBottom();
    } else {
      setShowScrollDown(true);
    }
  }, [messages]);

  const fetchMessages    = async () => {
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/conversations/${conversation.id}/messages`, {
        params: { limit: MESSAGES_PAGE }
      });
      setMessages(data.messages || []);
      setHasMoreMessages(!!data.hasMore);
      setOldestMessageId(data.oldestId);
    } catch (e) {}
    finally { setLoadingMessages(false); }
    api.put(`/conversations/${conversation.id}/read`).catch(() => {});
  };

  // "Carregar mensagens anteriores" — busca a página anterior à mais antiga que
  // está na tela e insere no topo, preservando a posição de leitura.
  const loadOlderMessages = async () => {
    if (loadingOlder || !oldestMessageId) return;
    setLoadingOlder(true);
    const el = messagesContainerRef.current;
    prependAnchorRef.current = el ? { altura: el.scrollHeight, topo: el.scrollTop } : null;
    skipAutoScrollRef.current = true;
    // Pedir histórico antigo é o oposto de acompanhar o fim: solta o pino, senão
    // o listener de mídia arrastaria o atendente de volta pro fim assim que uma
    // imagem antiga terminasse de carregar.
    pinBottomRef.current = false;
    try {
      const { data } = await api.get(`/conversations/${conversation.id}/messages`, {
        params: { limit: MESSAGES_PAGE, before: oldestMessageId }
      });
      const anteriores = data.messages || [];
      if (anteriores.length) {
        setMessages(prev => [...anteriores, ...prev]);
        setOldestMessageId(data.oldestId);
        setHasMoreMessages(!!data.hasMore);
      } else {
        setHasMoreMessages(false);
      }
    } catch (e) {
      prependAnchorRef.current = null;
      skipAutoScrollRef.current = false;
      showToast('Não foi possível carregar as mensagens anteriores', 'error');
    } finally {
      setLoadingOlder(false);
    }
  };

  // useLayoutEffect (e não useEffect) porque roda ANTES da pintura: o atendente
  // nunca chega a ver o salto, a rolagem já é corrigida no mesmo quadro.
  useLayoutEffect(() => {
    const ancora = prependAnchorRef.current;
    if (!ancora) return;
    prependAnchorRef.current = null;
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = ancora.topo + (el.scrollHeight - ancora.altura);
  }, [messages]);

  // Rede de segurança contra evento de socket perdido (2026-09-10).
  // Mesmo com websocket, uma queda breve de conexão pode engolir um
  // 'message:new' — foi assim que um PDF de grupo não apareceu na tela e só
  // surgiu ao recarregar. A cada 45s comparamos silenciosamente o que está na
  // tela com o servidor e só mexemos na lista se algo divergir. Sem spinner,
  // sem marcar como lido, sem piscar: se não houver diferença, nada acontece.
  const syncMessagesSilently = async () => {
    if (document.visibilityState !== 'visible') return;
    try {
      // (2026-09-25) Passou a pedir só a página mais recente. Antes baixava a
      // conversa INTEIRA a cada 45s — numa conversa de 1.070 mensagens eram
      // ~580 KB repetidos indefinidamente enquanto a aba ficasse aberta.
      const { data } = await api.get(`/conversations/${conversation.id}/messages`, {
        params: { limit: MESSAGES_PAGE }
      });
      const recentes = data.messages || [];
      if (!recentes.length) return;
      setMessages(prev => {
        // Preserva o que o atendente já carregou de mais antigo (botão "carregar
        // anteriores") e troca só a janela que o servidor acabou de mandar.
        const corte = recentes[0].id;
        const preservadas = prev.filter(m => m.id < corte);
        const atual = [...preservadas, ...recentes];
        const iguais = atual.length === prev.length &&
          atual[atual.length - 1]?.id === prev[prev.length - 1]?.id;
        if (iguais) return prev;
        console.warn(`[sync] divergência detectada: tela ${prev.length} × servidor ${atual.length} — corrigindo`);
        return atual;
      });
    } catch (e) { /* silencioso: é só uma checagem de consistência */ }
  };
  const fetchDepartments = async () => { try { setDepartments((await api.get('/departments')).data); } catch (e) {} };
  const fetchUsers       = async () => { try { setUsers((await api.get('/users/agents')).data); } catch (e) {} };
  const fetchNotes       = async () => { try { setNotes((await api.get(`/notes/conversation/${conversation.id}`)).data); } catch (e) {} };
  const fetchConversationTags = async () => { try { setTags((await api.get(`/tags/conversation/${conversation.id}`)).data); } catch (e) {} };
  const fetchAllTags     = async () => { try { setAllTags((await api.get('/tags')).data); } catch (e) {} };
  const fetchQuickReplies = async () => { try { setQuickReplies((await api.get('/quick-replies')).data); } catch (e) {} };
  const fetchOnlineUsers  = async () => { try { setOnlineUserIds(((await api.get('/users/online')).data.onlineUserIds || []).map(String)); } catch (e) {} };

  // ── Envio de mensagem ──
  const sendMessageRequest = async (text, opts = {}) => {
    return api.post(`/conversations/${conversation.id}/messages`, {
      messageText: text,
      replyToId: replyingTo?.id || null,
      confirmTemplateRepeat: !!opts.confirmTemplateRepeat
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const original = newMessage.trim();
    if (!original) return;
    setLoading(true);
    setNewMessage('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    // Corretor automático antes do envio (pode estar desligado pelo toggle).
    //
    // Why-incidente 2026-09-25: isto era `await autoCorrectText(original)`, uma
    // chamada de rede de ~500-600ms (só o servidor) travando o envio, com o
    // input JÁ limpo na linha acima — o texto sumia e nada aparecia por 1-2s.
    // Agora o caso normal é custo zero: a correção já foi calculada durante a
    // digitação. Sem cache, espera no máximo 700ms e envia o que tiver.
    let text = original;
    if (autoSpellcheck && original.length >= MIN_SPELL_LEN) {
      const cache = spellCacheRef.current;
      if (cache.source === original) {
        text = announceCorrection(cache);
      } else {
        // Digitou e enviou em menos de 600ms (antes do debounce disparar).
        // O teto evita trocar um delay grande por um delay só um pouco menor.
        const semCorrecao = { corrected: original, applied: [] };
        text = announceCorrection(await Promise.race([
          computeCorrection(original),
          new Promise(r => setTimeout(() => r(semCorrecao), 700))
        ]));
      }
    }
    try {
      await sendMessageRequest(text);
      setReplyingTo(null);
    } catch (err) {
      // 409 TEMPLATE_REPEAT — pede confirmação antes de reenviar
      if (err.response?.status === 409 && err.response.data?.code === 'TEMPLATE_REPEAT') {
        const d = err.response.data;
        const ok = window.confirm(
          `⚠️ ATENÇÃO — Possível disparo em massa detectado\n\n` +
          `Essa mesma mensagem já foi enviada pra ${d.recipients} contatos diferentes ` +
          `nas últimas ${d.windowHours}h.\n\n` +
          `Enviar texto idêntico pra muitos contatos é exatamente o que causou o ban de 24h ` +
          `no número da empresa. WhatsApp pode interpretar como disparo automatizado.\n\n` +
          `Recomendação: PERSONALIZE o texto pra esse contato específico antes de enviar.\n\n` +
          `Tem certeza que quer enviar essa mensagem mesmo assim?`
        );
        if (ok) {
          try {
            await sendMessageRequest(text, { confirmTemplateRepeat: true });
            setReplyingTo(null);
          } catch (err2) {
            setNewMessage(text);
            showToast(err2.response?.data?.message || 'Erro ao enviar mensagem', 'error');
          }
        } else {
          setNewMessage(text); // devolve o texto pro input
        }
      } else if (err.response?.status === 423) {
        setNewMessage(text);
        showToast(err.response.data?.message || 'Envio bloqueado pela proteção anti-ban', 'error');
      } else {
        setNewMessage(text);
        showToast('Erro ao enviar mensagem', 'error');
      }
    } finally {
      setLoading(false);
      setTimeout(() => { textareaRef.current?.focus(); }, 0);
    }
  };

  const uploadFile = async (file, caption = '') => {
    const form = new FormData();
    form.append('file', file);
    form.append('caption', caption);
    setLoading(true);
    setUploadProgress({ name: file.name || 'Arquivo', percent: 0, sizeMB: (file.size / 1048576).toFixed(1) });
    try {
      await api.post(`/conversations/${conversation.id}/media`, form, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const percent = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p => p ? { ...p, percent } : p);
        }
      });
      setNewMessage('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Erro ao enviar arquivo', 'error');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  // Envia uma lista de arquivos em série. Caption só vai no PRIMEIRO arquivo —
  // repetir a mesma legenda em todos seria spam.
  const uploadFiles = async (files) => {
    if (!files.length) return;
    const caption = newMessage || '';
    let first = true;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      // Sinaliza posição no progress quando há múltiplos
      if (files.length > 1) {
        setUploadProgress({
          name: `${f.name} (${i + 1}/${files.length})`,
          percent: 0,
          sizeMB: (f.size / 1048576).toFixed(1)
        });
      }
      await uploadFile(f, first ? caption : '');
      first = false;
    }
  };

  const handleUploadMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      await uploadFiles(files);
    } finally {
      e.target.value = '';
    }
  };

  // ── Arrastar e soltar arquivos no chat ──
  // dragDepth conta enter/leave dos elementos filhos: sem isso o overlay
  // piscava a cada vez que o cursor cruzava uma mensagem.
  const clearDrag = () => { dragDepth.current = 0; setDragActive(false); };

  const handleDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    // "copy" faz o cursor mostrar o ícone de cópia em vez do de "proibido"
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) clearDrag();
  };

  const handleDrop = async (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    clearDrag();

    if (reassigned) {
      showToast('Conversa atribuída a outro atendente — envio bloqueado', 'warning');
      return;
    }

    // Pastas viram entries de diretório (size 0) e o upload falharia sem
    // explicação — filtramos e avisamos.
    let hadDirectory = false;
    const items = e.dataTransfer.items ? Array.from(e.dataTransfer.items) : [];
    for (const item of items) {
      const entry = item.webkitGetAsEntry?.();
      if (entry?.isDirectory) hadDirectory = true;
    }

    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length) {
      if (hadDirectory) showToast('Pastas não podem ser enviadas — arraste os arquivos', 'warning');
      return;
    }

    const tooBig = dropped.filter(f => f.size > MAX_UPLOAD_MB * 1048576);
    const badType = dropped.filter(f => f.size <= MAX_UPLOAD_MB * 1048576 && !isAcceptedFile(f));
    const ok = dropped.filter(f => !tooBig.includes(f) && !badType.includes(f));

    if (tooBig.length) {
      showToast(`${tooBig.map(f => f.name).join(', ')}: acima do limite de ${MAX_UPLOAD_MB}MB`, 'error');
    }
    if (badType.length) {
      showToast(`${badType.map(f => f.name).join(', ')}: tipo de arquivo não permitido`, 'error');
    }
    if (hadDirectory) {
      showToast('Pastas foram ignoradas — arraste os arquivos', 'warning');
    }
    if (!ok.length) return;

    await uploadFiles(ok);
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    let first = true;
    for (const file of files) {
      const ext = (file.type.split('/')[1] || 'png').split(';')[0];
      const named = new File([file], file.name && file.name !== 'image.png'
        ? file.name
        : `clipboard-${Date.now()}-${files.indexOf(file)}.${ext}`, { type: file.type });
      await uploadFile(named, first ? (newMessage || '') : '');
      first = false;
    }
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const file = new File([blob], `audio.${ext}`, { type: mimeType });
        // Em vez de enviar imediatamente, mostra preview pra ouvir/aprovar/descartar
        const url = URL.createObjectURL(blob);
        setAudioPreview({
          url,
          file,
          sizeMB: (file.size / 1048576).toFixed(2)
        });
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch {
      showToast('Permissão de microfone negada', 'error');
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleDiscardAudio = () => {
    if (audioPreview?.url) URL.revokeObjectURL(audioPreview.url);
    setAudioPreview(null);
  };

  const handleSendAudioPreview = async () => {
    if (!audioPreview) return;
    const { file, sizeMB, url } = audioPreview;
    const form = new FormData();
    form.append('file', file);
    form.append('caption', '');
    setLoading(true);
    setUploadProgress({ name: 'Áudio', percent: 0, sizeMB });
    try {
      await api.post(`/conversations/${conversation.id}/media`, form, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const percent = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p => p ? { ...p, percent } : p);
        }
      });
      URL.revokeObjectURL(url);
      setAudioPreview(null);
    } catch {
      showToast('Erro ao enviar áudio', 'error');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const toggleSelectMsg = (id) => {
    setSelectedMsgIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedMsgIds(new Set());
  };

  const doDelete = async (forEveryone) => {
    setShowDeleteModal(false);
    const ids = [...selectedMsgIds];
    if (!ids.length) return;
    try {
      const res = await api.post('/conversations/messages/delete', { messageIds: ids, forEveryone });
      // Atualização local imediata (socket também notifica outros clients)
      setMessages(prev => prev.filter(m => !ids.includes(m.id)));
      exitSelectMode();
      if (forEveryone) {
        showToast(`${res.data.deleted} apagada(s) — ${res.data.deletedForEveryone} também removida(s) no WhatsApp`, 'success');
      } else {
        showToast(`${res.data.deleted} mensagem(ns) apagada(s) para você`, 'success');
      }
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao apagar mensagens', 'error');
    }
  };

  const hasOwnSelected = messages.some(m => selectedMsgIds.has(m.id) && m.sender_type === 'agent');

  // Mensagem elegível para edição: 1 selecionada, agent, texto, < 15min
  // Regra ÚNICA de "esta mensagem pode ser editada?".
  //
  // Why-incidente 2026-09-23: existiam DUAS regras divergentes pro mesmo recurso.
  // A do modo seleção não checava autoria; a do menu de contexto exigia
  // sender_id === usuário atual. Resultado: o mesmo botão aparecia por um caminho
  // e sumia pelo outro, e ninguém entendia o critério.
  //
  // O espelho aqui é o backend (editMessage): sender_type='agent' + message_type
  // 'text' + até 15min (limite do próprio WhatsApp). O backend NÃO exige ser o
  // autor — então não inventamos uma restrição mais dura sem motivo, mas também
  // não deixamos um atendente reescrever a fala de outro: só libera se for sua,
  // se você for admin/supervisor, ou se a mensagem não tem dono (enviada pelo
  // celular do atendente ou pelo bot — nesses casos sender_id é NULL no banco,
  // e mais da metade das mensagens de agente cai nesse caso).
  const isMsgEditable = (m) => {
    if (!m || m.sender_type !== 'agent') return false;
    if (!m.message_text) return false;
    if (m.media_url) return false; // backend só edita message_type='text'

    // Number.isFinite protege contra created_at ausente/inválido: NaN reprovaria
    // silenciosamente em qualquer comparação e o botão sumiria sem explicação.
    const ageMin = (Date.now() - new Date(m.created_at).getTime()) / 60000;
    if (!Number.isFinite(ageMin) || ageMin > 15) return false;

    if (isAdminUser) return true;
    if (m.sender_id == null) return true; // sem dono definido
    return String(m.sender_id) === String(currentUser.id);
  };

  const editableMessage = (() => {
    if (selectedMsgIds.size !== 1) return null;
    const id = [...selectedMsgIds][0];
    const m = messages.find(x => x.id === id);
    return isMsgEditable(m) ? m : null;
  })();

  // ── Menu de contexto por mensagem (hover + botão direito) ──
  const openMsgMenu = (msg, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const x = Math.min(ev.clientX, window.innerWidth - 220);
    const y = Math.min(ev.clientY, window.innerHeight - 220);
    setMsgMenu({ msg, x, y });
  };
  const closeMsgMenu = () => setMsgMenu(null);

  useEffect(() => {
    if (!msgMenu) return;
    const onDoc = (e) => {
      if (e.target.closest('.msg-menu')) return;
      closeMsgMenu();
    };
    const onKey = (e) => { if (e.key === 'Escape') closeMsgMenu(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', closeMsgMenu, true);
    window.addEventListener('resize', closeMsgMenu);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', closeMsgMenu, true);
      window.removeEventListener('resize', closeMsgMenu);
    };
  }, [msgMenu]);

  // Ações disparadas pelo menu — operam sobre msgMenu.msg
  const replyToMsg = (m) => {
    setReplyingTo({
      id: m.id,
      text: (m.message_text || '').replace(/^\*[^*]+\*\s*\n?/, '') || `[${m.media_type || 'mídia'}]`,
      senderName: m.sender_type === 'agent'
        ? (m.sender_name || 'Você')
        : (nameOverride || conversation.contact_name || 'Cliente'),
      senderType: m.sender_type
    });
    closeMsgMenu();
    setTimeout(() => textareaRef.current?.focus(), 50);
  };
  const editMsg = (m) => {
    closeMsgMenu();
    const stripped = (m.message_text || '').replace(/^\*[^*]+\*\s*\n?/, '');
    setEditModal({ id: m.id, text: stripped });
  };
  const deleteMsgSingle = (m) => {
    closeMsgMenu();
    setSelectedMsgIds(new Set([m.id]));
    setShowDeleteModal(true);
  };
  const forwardMsgSingle = async (m) => {
    closeMsgMenu();
    setSelectedMsgIds(new Set([m.id]));
    openForwardModal();
  };
  const copyMsgText = async (m) => {
    closeMsgMenu();
    const t = (m.message_text || '').replace(/^\*[^*]+\*\s*\n?/, '');
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      showToast('Texto copiado', 'success');
    } catch {
      showToast('Não foi possível copiar', 'error');
    }
  };

  // (isMsgEditable foi unificada e movida pra junto de editableMessage — ver lá.)

  // Apagar permitido pelo próprio autor (ou admin/supervisor)
  const canDelete = (m) => {
    if (!m) return false;
    if (currentUser.role === 'admin' || currentUser.role === 'supervisor') return true;
    return String(m.sender_id) === String(currentUser.id);
  };

  const openEdit = () => {
    if (!editableMessage) return;
    // Mensagens enviadas pelo agente vêm com o prefixo "*Nome (Setor):*\n" — remove pra editar só o texto.
    const stripped = (editableMessage.message_text || '').replace(/^\*[^*]+\*\s*\n?/, '');
    setEditModal({ id: editableMessage.id, text: stripped });
  };

  const submitEdit = async () => {
    if (!editModal?.text?.trim()) return;
    const id = editModal.id;
    const text = editModal.text.trim();
    setEditModal(null);
    try {
      await api.put(`/conversations/messages/${id}`, { text });
      exitSelectMode();
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao editar', 'error');
    }
  };

  const openForwardModal = async () => {
    setForwardLoading(true);
    setShowForwardModal(true);
    try {
      const [open, pending] = await Promise.all([
        api.get('/conversations', { params: { status: 'open' } }),
        api.get('/conversations', { params: { status: 'pending' } }),
      ]);
      const all = [...open.data, ...pending.data]
        .filter(c => c.id !== conversation.id);
      setForwardConvs(all);
    } catch {
      showToast('Erro ao carregar conversas', 'error');
    } finally {
      setForwardLoading(false);
    }
  };

  const doForward = async (targetId) => {
    setShowForwardModal(false);
    try {
      await api.post('/conversations/forward', {
        messageIds: [...selectedMsgIds],
        targetConversationId: targetId
      });
      showToast(`${selectedMsgIds.size} mensagem(ns) encaminhada(s)!`, 'success');
      exitSelectMode();
    } catch {
      showToast('Erro ao encaminhar mensagens', 'error');
    }
  };

  const selectModeRef = useRef(false);
  selectModeRef.current = selectMode;

  const touchStartRef = useRef({ x: 0, y: 0 });

  const handleMsgTouchStart = (id, e) => {
    const t = e.touches?.[0];
    touchStartRef.current = { x: t?.clientX || 0, y: t?.clientY || 0 };
    longPressRef.current = setTimeout(() => {
      if (!selectModeRef.current) {
        setSelectMode(true);
        setSelectedMsgIds(new Set([id]));
      }
    }, 650);
  };

  const handleMsgTouchEnd = () => {
    clearTimeout(longPressRef.current);
  };

  const handleMsgTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - touchStartRef.current.x);
    const dy = Math.abs(t.clientY - touchStartRef.current.y);
    if (dx > 8 || dy > 8) clearTimeout(longPressRef.current);
  };

  const handleMessageChange = (value) => {
    setNewMessage(value);
    setShowQuickMenu(value.startsWith('/') && value.length > 0);
  };

  const applyQuickReply = async (qr) => {
    setShowQuickMenu(false);
    // Sem arquivo: comportamento antigo — preenche o composer
    if (!qr.media_url && !qr.file_name) {
      const msg = qr.message || '';
      setNewMessage(msg);
      // Devolve foco pro textarea com cursor no fim — assim o próximo Enter envia
      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        try { ta.setSelectionRange(msg.length, msg.length); } catch {}
        autoGrowTextarea(ta);
      }, 0);
      return;
    }
    // Com arquivo: envia direto (arquivo já está no servidor)
    setLoading(true);
    setUploadProgress({ name: qr.file_name || 'Resposta rápida', percent: 50, sizeMB: '–' });
    try {
      await api.post(`/quick-replies/${qr.id}/send`, { conversationId: conversation.id }, { timeout: 0 });
      setNewMessage('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Erro ao enviar resposta rápida', 'error');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const filteredQuickReplies = newMessage.startsWith('/')
    ? quickReplies.filter(q => q.shortcut?.toLowerCase().startsWith(newMessage.toLowerCase()))
    : [];

  // Navegação por teclado nas respostas rápidas (↑↓ Enter Esc)
  const [qrHighlight, setQrHighlight] = useState(0);
  // Reseta o destaque quando dropdown/panel abrem ou a lista filtrada muda
  useEffect(() => { setQrHighlight(0); }, [showQuickMenu, showQuickPanel, filteredQuickReplies.length]);

  // Rola o item destacado pra dentro do viewport quando navegamos com ↑↓
  useEffect(() => {
    if (!showQuickMenu && !showQuickPanel) return;
    const selector = showQuickMenu
      ? '.quick-reply-dropdown .quick-reply-item'
      : '.quick-panel-list .quick-panel-item';
    const items = document.querySelectorAll(selector);
    const el = items[qrHighlight];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [qrHighlight, showQuickMenu, showQuickPanel]);

  // Lista atual considerando se é dropdown (filtrado) ou panel (todos)
  const qrCurrentList = showQuickMenu
    ? filteredQuickReplies.slice(0, 6)
    : (showQuickPanel ? quickReplies : []);

  // Atalhos globais de teclado pro panel — o dropdown usa o onKeyDown do textarea
  useEffect(() => {
    if (!showQuickPanel) return;
    const onKey = (e) => {
      if (!qrCurrentList.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQrHighlight(i => (i + 1) % qrCurrentList.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQrHighlight(i => (i - 1 + qrCurrentList.length) % qrCurrentList.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const q = qrCurrentList[qrHighlight];
        if (q) { applyQuickReply(q); setShowQuickPanel(false); }
      } else if (e.key === 'Escape') {
        setShowQuickPanel(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showQuickPanel, qrCurrentList, qrHighlight]);


  const handleAccept = async () => {
    const wasClosed = convStatus === 'closed';
    try {
      await api.put(`/conversations/${conversation.id}/accept`);
      setStatusOverride('open');
      showToast(
        wasClosed
          ? 'Atendimento reaberto e atribuído a você'
          : 'Atendimento aceito com sucesso!',
        'success'
      );
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao aceitar', 'error');
    }
  };

  // ── Abrir conversa a partir de um contato compartilhado ──
  // Mesmo caminho da agenda (POST /conversations): passa pela quota de
  // "nova conversa" e pelos gates de segurança. Erro vira toast.
  const startChatWithContact = async (phone, name) => {
    if (startingContact) return;
    setStartingContact(phone.number);
    try {
      const res = await api.post('/conversations', {
        contactNumber: phone.number,
        contactName: name || phone.display || phone.number
      });
      const conv = await api.get(`/conversations/${res.data.id}`);
      if (onStartConversation) onStartConversation(conv.data);
      else showToast(`Conversa aberta (id ${res.data.id})`, 'success');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao abrir conversa', 'error');
    } finally {
      setStartingContact(null);
    }
  };

  // ── Enviar contato (vCard) ──
  const handleSendContact = async (contacts) => {
    try {
      await api.post(`/conversations/${conversation.id}/contact`, { contacts });
      setShowContactPicker(false);
      // A mensagem chega pelo socket (message:new) como qualquer outro envio.
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao enviar contato', 'error');
    }
  };

  // ── Transferência via modal ──
  const doTransfer = async ({ deptId, agentId, reason }) => {
    setShowTransferModal(false);
    try {
      await api.put(`/conversations/${conversation.id}/transfer`, {
        agentId: agentId || null,
        departmentId: deptId || null,
        reason
      });
      fetchNotes();
      showToast('Conversa transferida!', 'success');
    } catch (e) {
      showToast('Erro ao transferir', 'error');
    }
  };

  // ── Resolver via modal ──
  const doResolve = async () => {
    setConfirmResolve(false);
    try {
      await api.put(`/conversations/${conversation.id}/resolve`);
      showToast('Atendimento encerrado! O cliente foi notificado.', 'success');
    } catch (e) {
      showToast('Erro ao resolver conversa', 'error');
    }
  };

  // ── Anti-loop bot-a-bot: marca/desmarca o contato como bot ──
  const handleToggleBot = async () => {
    const next = !isBot;
    setIsBot(next);                 // otimista
    if (next) setAutoReplyBlocked(false); // marcar bot já libera a trava de loop
    try {
      await api.put(`/conversations/${conversation.id}/bot`, { isBot: next });
      showToast(
        next
          ? 'Contato marcado como bot — encerramento e chatbot automáticos desativados para ele'
          : 'Marcação de bot removida',
        'success'
      );
    } catch (e) {
      setIsBot(!next);              // reverte
      showToast('Erro ao atualizar marcação de bot', 'error');
    }
  };

  // ── Anti-loop bot-a-bot: retoma a automação travada pelo detector de loop ──
  const handleResumeAutomation = async () => {
    setAutoReplyBlocked(false);     // otimista
    try {
      await api.put(`/conversations/${conversation.id}/resume-automation`);
      showToast('Automação retomada para esta conversa', 'success');
    } catch (e) {
      setAutoReplyBlocked(true);    // reverte
      showToast('Erro ao retomar automação', 'error');
    }
  };

  // ── Notas ──
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/notes/conversation/${conversation.id}`, { content: newNote });
      setNewNote('');
      fetchNotes();
    } catch (e) {
      showToast('Erro ao salvar nota', 'error');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Apagar esta nota?')) return;
    try {
      await api.delete(`/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao apagar nota', 'error');
    }
  };

  // ── Tags ──
  const addTag = async (tagId) => {
    try { await api.post(`/tags/conversation/${conversation.id}`, { tagId }); fetchConversationTags(); } catch (e) {}
  };
  const removeTag = async (tagId) => {
    try { await api.delete(`/tags/conversation/${conversation.id}/${tagId}`); fetchConversationTags(); } catch (e) {}
  };

  // ── Agendamentos ──
  const scheduleMessage = async () => {
    if (!scheduledAt || !scheduledText.trim()) {
      showToast('Preencha a data e a mensagem', 'warning');
      return;
    }
    // datetime-local vem como "YYYY-MM-DDTHH:mm" sem TZ; new Date trata como hora local do navegador
    const iso = new Date(scheduledAt).toISOString();
    if (new Date(iso).getTime() <= Date.now()) {
      showToast('Escolha uma data/hora no futuro', 'warning');
      return;
    }
    try {
      await api.post(`/scheduled-messages/conversation/${conversation.id}`, {
        messageText: scheduledText,
        scheduledAt: iso
      });
      setScheduledAt(''); setScheduledText('');
      showToast('Mensagem agendada!', 'success');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao agendar mensagem', 'error');
    }
  };

  if (!conversation) return null;

  const contactName = nameOverride || conversation.contact_name || 'Sem Nome';

  return (
    <div
      className={`conversation-detail ${selectMode ? 'select-mode' : ''} ${dragActive ? 'drag-active' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Arrastar arquivo de fora e soltar em qualquer ponto da conversa envia.
          A legenda digitada no campo vai junto, no primeiro arquivo. */}
      {dragActive && (
        <div className="drop-overlay">
          <div className="drop-overlay-box">
            <Paperclip size={30} />
            <strong>Solte para enviar</strong>
            <span>Imagens, vídeos, áudios e documentos · até {MAX_UPLOAD_MB}MB cada</span>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="detail-header">
        {onBack && (
          <button className="back-btn" onClick={onBack}>
            <ChevronLeft size={18} /> Voltar
          </button>
        )}
        <div className="contact-info">
          {/* Clicar na foto abre no lightbox — mesma visualização das imagens
              da conversa. Só quando existe foto: o avatar de iniciais não tem
              o que ampliar. */}
          {/* Wrapper ancora o ponto de atividade no canto do avatar; mantém a
              ordem img → div porque o onError depende de nextSibling. */}
          <span className="contact-avatar-wrap">
            {conversation.profile_photo_url ? (
              <img
                className="contact-avatar contact-avatar-photo contact-avatar-clickable"
                src={conversation.profile_photo_url}
                alt={contactName}
                title="Ver foto de perfil"
                onClick={() => setLightboxUrl(conversation.profile_photo_url)}
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
              />
            ) : null}
            <div className="contact-avatar" style={{ background: avatarColor(contactName), display: conversation.profile_photo_url ? 'none' : 'flex' }}>
              {getInitials(contactName)}
            </div>
            {!conversation.is_group && activityLevel(conversation.last_client_message_at) && (
              <span
                className={`contact-activity-dot contact-activity-${activityLevel(conversation.last_client_message_at)}`}
                title={activityLabel(conversation.last_client_message_at)}
              />
            )}
          </span>
          <div className="contact-text">
            <h2>{contactName}</h2>
            <p className="contact-number">
              {conversation.contact_number}
              {/* "ativo há X" = última mensagem DELE. Não é status online do
                  WhatsApp — o title explicita isso pra equipe não ler errado. */}
              {!conversation.is_group && activityHeadline(conversation.last_client_message_at) && (
                <span
                  className="contact-activity-text"
                  title={activityLabel(conversation.last_client_message_at)}
                >
                  {' · '}{activityHeadline(conversation.last_client_message_at)}
                </span>
              )}
            </p>
            <div className="conv-tags-row">
              {tags.map(t => (
                <span key={t.id} className="tag-chip" style={{ background: t.color || '#4F46E5' }}>
                  {t.name}
                  <button onClick={() => removeTag(t.id)} title="Remover">×</button>
                </span>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* ── Action bar ── */}
      <div className="action-bar">
        {/* Aceitar/Transferir/Resolver não fazem sentido em grupos */}
        {!conversation.is_group && convStatus === 'pending' && (
          <button className="action-btn accept" onClick={handleAccept}>
            <Hand size={14}/> Aceitar
          </button>
        )}
        {/* Resolvida fica sem atendente: qualquer um do setor pode pegar pra si
            (vai pro Atendendo dele) ou transferir (cai no Aguardando de quem
            recebeu). Resolver não aparece — já está resolvida. */}
        {!conversation.is_group && convStatus === 'closed' && (
          <button className="action-btn accept" onClick={handleAccept}
            title="Reabre o atendimento no seu Atendendo">
            <Hand size={14}/> Pegar para mim
          </button>
        )}
        {!conversation.is_group && (
          <button className="action-btn transfer" onClick={() => setShowTransferModal(true)}>
            <ArrowRightLeft size={14}/> Transferir
          </button>
        )}
        {!conversation.is_group && convStatus !== 'closed' && (
          <button className="action-btn resolve" onClick={() => setConfirmResolve(true)}>
            <CheckCircle size={14}/> Resolver
          </button>
        )}
        {/* Anti-loop bot-a-bot: marca o contato como bot (persiste pro número).
            Disponível mesmo em conversas fechadas, pra marcar um bot que insiste
            em reabrir o atendimento. */}
        {!conversation.is_group && (
          <button
            className={`action-btn ${isBot ? 'active' : ''}`}
            onClick={handleToggleBot}
            title={isBot
              ? 'Reativar respostas automáticas para este contato'
              : 'Marcar como bot: desativa mensagem de encerramento e chatbot para este contato (evita loop)'}
          >
            <Bot size={14}/> {isBot ? 'É um bot' : 'Marcar bot'}
          </button>
        )}
        {/* Botão "Fixar" foi movido pro menu de contexto da lista (3 pontinhos).
            A lógica/estado foi mantida no componente caso outras telas reutilizem. */}
        {!!conversation.is_group && (
          <button
            className={`action-btn ${isMuted ? 'active' : ''}`}
            onClick={handleToggleMute}
            title={isMuted ? 'Reativar notificações' : 'Silenciar notificações'}
          >
            {isMuted ? <BellOff size={14}/> : <Bell size={14}/>} {isMuted ? 'Silenciado' : 'Silenciar'}
          </button>
        )}
        {!!conversation.is_group && (
          <button
            className={`action-btn ${showGroupPanel ? 'active' : ''}`}
            onClick={() => {
              const next = !showGroupPanel;
              setShowGroupPanel(next);
              if (next && participants.length === 0) fetchParticipants(false);
            }}
            title="Configurações do grupo"
          >
            <Settings size={14}/> Grupo
          </button>
        )}
        <div className="action-spacer" />
        <button
          className={`action-btn ${showNotes ? 'active' : ''} ${notes.length > 0 ? 'has-notes' : ''}`}
          onClick={() => setShowNotes(!showNotes)}
          title={notes.length > 0 ? `${notes.length} nota(s) anexada(s)` : 'Notas internas'}
        >
          <StickyNote size={14}/> Notas
          {notes.length > 0 && <span className="notes-badge">{notes.length}</span>}
        </button>
        <button className={`action-btn ${showTags ? 'active' : ''}`} onClick={() => setShowTags(!showTags)}>
          <TagIcon size={14}/> Tags
        </button>
        <button className={`action-btn ${showSchedule ? 'active' : ''}`} onClick={() => setShowSchedule(!showSchedule)}>
          <Clock size={14}/> Agendar
        </button>
        <button className={`action-btn ${selectMode ? 'active' : ''}`} onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}>
          <Forward size={14}/> {selectMode ? 'Cancelar' : 'Selecionar'}
        </button>
      </div>

      {/* ── Anti-loop bot-a-bot: aviso de automação pausada ── */}
      {autoReplyBlocked && !isBot && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '10px 16px', background: '#FEF3C7', color: '#92400E',
          borderBottom: '1px solid #FCD34D', fontSize: 13, flexWrap: 'wrap'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} />
            Possível loop com outro bot detectado — respostas automáticas pausadas nesta conversa.
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleResumeAutomation}
              style={{ padding: '5px 12px', border: '1px solid #92400E', background: 'transparent', color: '#92400E', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >
              Retomar automação
            </button>
            <button
              onClick={handleToggleBot}
              style={{ padding: '5px 12px', border: 'none', background: '#92400E', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >
              É um bot
            </button>
          </span>
        </div>
      )}

      {/* ── Painéis laterais ── */}
      {showNotes && (
        <div className="side-panel notes-panel">
          <h4>Notas internas</h4>
          {notes.length === 0 && <p className="notes-empty">Nenhuma nota ainda.</p>}
          {notes.map(n => (
            <div key={n.id} className="note-item">
              <button
                className="note-delete-btn"
                title="Apagar nota"
                onClick={() => deleteNote(n.id)}
              >
                <X size={13} />
              </button>
              <strong>{n.user_name || 'Equipe'}</strong>
              <p>{n.content}</p>
              <span>{new Date(n.created_at).toLocaleString()}</span>
            </div>
          ))}
          <div className="note-compose">
            <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Nota visível apenas para a equipe..." spellCheck={true} lang="pt-BR" />
            <button onClick={addNote}><FileText size={13}/> Salvar nota</button>
          </div>
        </div>
      )}

      {showTags && (
        <div className="side-panel tags-panel">
          <h4>Aplicar tags</h4>
          <div className="tags-grid">
            {allTags.map(t => {
              const applied = tags.some(x => x.id === t.id);
              return (
                <button
                  key={t.id}
                  className={`tag-option ${applied ? 'applied' : ''}`}
                  style={{ borderColor: t.color || '#4F46E5', background: applied ? (t.color || '#4F46E5') : undefined }}
                  onClick={() => applied ? removeTag(t.id) : addTag(t.id)}
                >
                  {t.name} {applied && '✓'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showSchedule && (
        <div className="side-panel schedule-panel">
          <h4>Agendar mensagem de follow-up</h4>
          <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
          <textarea placeholder="Mensagem a enviar..." value={scheduledText} onChange={e => setScheduledText(e.target.value)} spellCheck={true} lang="pt-BR" />
          <button onClick={scheduleMessage}>Agendar envio</button>
        </div>
      )}

      {showGroupPanel && !!conversation.is_group && (
        <div className="side-panel group-panel">
          <h4><Settings size={14}/> Configurações do grupo</h4>

          {!isAdminUser && (
            <p className="group-panel-note">
              ℹ️ Apenas admin/supervisor pode renomear, atribuir setor ou sair.
              Você pode visualizar os participantes.
            </p>
          )}

          <div className="group-panel-section">
            <label>Nome do grupo</label>
            <div className="group-panel-row">
              <input
                type="text"
                value={groupNewName}
                onChange={e => setGroupNewName(e.target.value)}
                disabled={!isAdminUser}
                placeholder="Nome"
              />
              {isAdminUser && (
                <button onClick={handleRenameGroup} className="group-panel-btn">
                  Salvar
                </button>
              )}
            </div>
          </div>

          <div className="group-panel-section">
            <label>Setor responsável</label>
            <div className="group-panel-row">
              <select
                value={groupDeptId}
                onChange={e => setGroupDeptId(e.target.value)}
                disabled={!isAdminUser}
              >
                <option value="">— Sem setor (todos os atendentes) —</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              {isAdminUser && (
                <button onClick={handleSetDepartment} className="group-panel-btn">
                  Salvar
                </button>
              )}
            </div>
            <small className="group-panel-help">
              Quando atribuído, só atendentes deste setor enxergam o grupo.
            </small>
          </div>

          <div className="group-panel-section">
            <div className="group-panel-row" style={{ justifyContent: 'space-between' }}>
              <label style={{ margin: 0 }}>
                <Users size={13} style={{ verticalAlign: 'middle' }}/> Participantes ({participants.length})
              </label>
              <button onClick={() => fetchParticipants(true)} className="group-panel-btn-ghost" disabled={loadingParticipants}>
                <RefreshCw size={12}/> Atualizar
              </button>
            </div>
            <div className="group-participants">
              {loadingParticipants && <p className="group-panel-empty">Carregando...</p>}
              {!loadingParticipants && participants.length === 0 && (
                <p className="group-panel-empty">Clique em Atualizar para sincronizar.</p>
              )}
              {!loadingParticipants && participants.map(p => {
                const isLid = (p.jid || '').endsWith('@lid');
                const num = (p.jid || '').replace(/@.*/, '').replace(/[^\d]/g, '');
                // Linked-ID: o WhatsApp não expõe o número real. Mostra o pushName
                // (capturado de mensagens passadas) ou um placeholder + últimos dígitos.
                const display = (p.push_name && p.push_name.trim())
                  ? p.push_name
                  : isLid
                    ? `Participante ${num ? `…${num.slice(-4)}` : ''}`.trim()
                    : (num ? `+${num}` : p.jid);
                return (
                  <div key={p.jid} className="group-participant" title={p.jid}>
                    <span className="group-participant-name">{display}</span>
                    {!!p.is_super_admin && <span className="group-participant-badge super">Dono</span>}
                    {!!p.is_admin && !p.is_super_admin && <span className="group-participant-badge admin">Admin</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {isAdminUser && (
            <div className="group-panel-section danger">
              <button onClick={handleLeaveGroup} className="group-panel-btn-danger">
                <LogOut size={14}/> Sair do grupo
              </button>
              <small className="group-panel-help">
                O número conectado deixará de ser participante. Para voltar, peça
                pra um admin do grupo readicioná-lo no WhatsApp.
              </small>
            </div>
          )}
        </div>
      )}

      {/* ── Mensagens ── */}
      <div className="messages-container" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
        {/* Skeleton só na PRIMEIRA carga (ainda sem mensagens). Refreshes em
           background (focus, reconect, visibility) mantêm o conteúdo na tela
           pra não "piscar" toda vez que troca de aba ou recebe evento. */}
        {/* Só aparece quando há histórico além da página carregada. Botão, e não
            rolagem automática, foi escolha deliberada: carregar sozinho ao tocar
            o topo é onde nasce o bug de tela pulando, e esta é a tela em que a
            equipe passa o dia. */}
        {hasMoreMessages && !(loadingMessages && messages.length === 0) && (
          <div className="load-older-wrap">
            <button
              className="load-older-btn"
              onClick={loadOlderMessages}
              disabled={loadingOlder}
            >
              {loadingOlder ? 'Carregando…' : 'Carregar mensagens anteriores'}
            </button>
          </div>
        )}
        {loadingMessages && messages.length === 0 ? (
          <>
            <div className="msg-skeleton msg-skeleton-client"><div className="skel-bubble" /></div>
            <div className="msg-skeleton msg-skeleton-agent"><div className="skel-bubble skel-bubble-agent" /></div>
            <div className="msg-skeleton msg-skeleton-client"><div className="skel-bubble skel-bubble-short" /></div>
            <div className="msg-skeleton msg-skeleton-agent"><div className="skel-bubble" /></div>
          </>
        ) : messages.map((msg, idx) => {
          const curDate = new Date(msg.created_at);
          const prevDate = idx > 0 ? new Date(messages[idx - 1].created_at) : null;
          const showDateSep = !prevDate || !isSameDay(prevDate, curDate);
          const dateSep = showDateSep ? (
            <div key={`sep-${msg.id}`} className="date-separator">
              <span>{formatDateLabel(curDate)}</span>
            </div>
          ) : null;

          if (msg.is_system) {
            return (
              <React.Fragment key={msg.id}>
                {dateSep}
                <div className="message-system" data-msg-id={msg.id}>
                  <span className="message-system-bubble">{msg.message_text}</span>
                  <span className="message-system-time">
                    {curDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </React.Fragment>
            );
          }

          // Apagada "para todos": fica o rastro, como no WhatsApp. Sem conteúdo
          // (o servidor não manda mais), sem menu de ações e fora da seleção —
          // não há o que editar, citar ou encaminhar.
          if (msg.deleted_for_everyone) {
            return (
              <React.Fragment key={msg.id}>
                {dateSep}
                <div className={`message ${msg.sender_type}`} data-msg-id={msg.id}>
                  <div className="message-bubble msg-deleted">
                    <span className="msg-deleted-text">
                      {/* Neutro de propósito: quem apagou pode ter sido outro
                          atendente da equipe, não necessariamente quem está vendo. */}
                      <Ban size={14} /> Mensagem apagada
                    </span>
                    <span className="message-time">
                      {curDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={msg.id}>
              {dateSep}
          <div
            className={`message ${msg.sender_type}${msg.is_internal_note ? ' internal' : ''}${msg.is_sticker ? ' is-sticker' : ''}${selectedMsgIds.has(msg.id) ? ' msg-selected' : ''}`}
            data-msg-id={msg.id}
            onClick={() => selectMode && toggleSelectMsg(msg.id)}
            onContextMenu={(e) => { if (!selectMode) openMsgMenu(msg, e); }}
            onTouchStart={(e) => handleMsgTouchStart(msg.id, e)}
            onTouchMove={handleMsgTouchMove}
            onTouchEnd={handleMsgTouchEnd}
            onTouchCancel={handleMsgTouchEnd}
            style={selectMode ? { cursor: 'pointer', userSelect: 'none' } : {}}
          >
            <div className="message-bubble">
              {/* Chevron de hover — abre o menu de ações da mensagem */}
              {!selectMode && (
                <button
                  className="msg-menu-trigger"
                  onClick={(e) => { e.stopPropagation(); openMsgMenu(msg, e); }}
                  title="Mais opções"
                  aria-label="Opções da mensagem"
                >
                  <ChevronDown size={14}/>
                </button>
              )}
              {!!msg.is_sticker && msg.sender_type === 'client' && (
                <button
                  className="sticker-save-btn"
                  onClick={(e) => { e.stopPropagation(); handleSaveSticker(msg.id); }}
                  title="Salvar figurinha na biblioteca"
                >
                  <Bookmark size={13}/>
                </button>
              )}
              {msg.reply_to_id && msg.quoted_text && (
                <div
                  className="msg-quote"
                  onClick={() => {
                    const el = document.querySelector(`[data-msg-id="${msg.reply_to_id}"]`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      el.classList.add('msg-quote-flash');
                      setTimeout(() => el.classList.remove('msg-quote-flash'), 1500);
                    }
                  }}
                >
                  <span className="msg-quote-sender">
                    {msg.quoted_sender_type === 'agent'
                      ? (msg.quoted_sender_name || 'Atendente')
                      : (nameOverride || conversation.contact_name || 'Cliente')}
                  </span>
                  <span className="msg-quote-text">
                    {(msg.quoted_text || '').replace(/^\*[^*]+\*\s*\n?/, '').substring(0, 120)}
                    {msg.quoted_text && msg.quoted_text.length > 120 ? '…' : ''}
                  </span>
                </div>
              )}
              {msg.sender_type === 'agent' && msg.sender_name && (
                <strong className="agent-sender-name">{msg.sender_name}</strong>
              )}
              {msg.sender_type === 'client' && msg.participant_name && (
                <strong
                  className="participant-sender-name"
                  style={{ color: participantColor(msg.participant_name) }}
                >
                  {msg.participant_name}
                </strong>
              )}
              {msg.media_type === 'location' && (() => {
                const loc = parsePayload(msg.contact_payload);
                if (!loc?.latitude) return null;
                const coords = `${loc.latitude},${loc.longitude}`;
                return (
                  <a
                    className="location-card"
                    href={`https://www.google.com/maps/search/?api=1&query=${coords}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Abrir no Google Maps"
                  >
                    <div className="location-card-pin"><MapPin size={20} /></div>
                    <div className="location-card-info">
                      <strong>{loc.name || (loc.live ? 'Localização em tempo real' : 'Localização')}</strong>
                      {loc.address && <span>{loc.address}</span>}
                      <span className="location-card-coords">{Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}</span>
                    </div>
                  </a>
                );
              })()}
              {msg.media_type === 'poll' && (() => {
                const poll = parsePayload(msg.contact_payload);
                if (!poll?.name) return null;
                return (
                  <div className="poll-card">
                    <strong className="poll-card-title"><BarChart3 size={14} /> {poll.name}</strong>
                    {(poll.options || []).map((o, oi) => (
                      <span className="poll-card-option" key={oi}>{o}</span>
                    ))}
                    <small className="poll-card-hint">Enquete — responda pelo WhatsApp</small>
                  </div>
                );
              })()}
              {msg.media_type === 'contact' && parseContacts(msg.contact_payload).map((c, ci) => (
                <div className="contact-card" key={ci}>
                  <div className="contact-card-head">
                    <div
                      className="contact-card-avatar"
                      style={{ background: avatarColor(c.name || '') }}
                    >
                      {getInitials(c.name || '')}
                    </div>
                    <div className="contact-card-info">
                      <strong className="contact-card-name">{c.name}</strong>
                      {(c.phones || []).map((p, pi) => (
                        <span className="contact-card-phone" key={pi}>{p.display}</span>
                      ))}
                    </div>
                  </div>
                  <div className="contact-card-actions">
                    {(c.phones || []).slice(0, 1).map((p, pi) => (
                      <button
                        key={pi}
                        className="contact-card-btn"
                        disabled={startingContact === p.number}
                        onClick={() => startChatWithContact(p, c.name)}
                      >
                        <MessageCircle size={15} />
                        {startingContact === p.number ? 'Abrindo…' : 'Conversar'}
                      </button>
                    ))}
                    {msg.media_url && (
                      <a
                        href={msg.media_url}
                        download={msg.file_name || 'contato.vcf'}
                        className="contact-card-btn contact-card-btn-ghost"
                      >
                        <Download size={15} />
                        Salvar
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {msg.media_url && msg.media_type !== 'contact' && (
                <div className="media-preview">
                  {msg.media_type?.startsWith('image') ? (
                    <div className="image-wrap">
                      {/* loading="lazy" (2026-09-28): sem isto, abrir um chat
                          disparava o download de TODAS as imagens da página de
                          100 mensagens de uma vez — dezenas de requisições
                          simultâneas travando a troca de conversa. Agora o
                          navegador só baixa as que chegam perto da viewport.
                          decoding="async" tira a decodificação da thread
                          principal, que é o que congelava a rolagem. */}
                      <img
                        src={msg.media_url}
                        alt="mídia"
                        loading="lazy"
                        decoding="async"
                        style={{ cursor: selectMode ? 'pointer' : 'zoom-in' }}
                        onClick={() => !selectMode && setLightboxUrl(msg.media_url)}
                      />
                      <a href={msg.media_url} download className="video-download-btn" title="Baixar imagem" onClick={e => e.stopPropagation()}>
                        <Download size={18}/>
                      </a>
                    </div>
                  ) : msg.media_type?.startsWith('video') ? (
                    <div className="video-player-wrap">
                      {/* preload="none": vídeo é o anexo mais pesado e quase
                          nunca é assistido de imediato. Sem isto, cada vídeo do
                          histórico começava a baixar sozinho ao abrir o chat. */}
                      <video src={msg.media_url} controls playsInline preload="none" className="video-player" />
                      <a href={msg.media_url} download className="video-download-btn" title="Baixar vídeo" onClick={e => e.stopPropagation()}>
                        <Download size={18}/>
                      </a>
                    </div>
                  ) : msg.media_type?.startsWith('audio') ? (
                    /* metadata (e não none) porque em áudio a duração aparece no
                       player e é informação útil — é um fetch pequeno, ao
                       contrário do vídeo. */
                    <audio src={msg.media_url} controls preload="metadata" />
                  ) : (
                    <a href={msg.media_url} target="_blank" rel="noreferrer" className="doc-link" download={msg.file_name || undefined}>
                      📎 {msg.file_name || 'Documento'}
                      {msg.media_type && <span className="doc-mime"> ({(msg.media_type.split('/')[1] || 'arquivo').toUpperCase()})</span>}
                    </a>
                  )}
                </div>
              )}
              {/* No contato, message_text é só o resumo (nome — telefone) que
                  alimenta prévia/busca — o card acima já mostra tudo. */}
              {msg.message_text && msg.media_type !== 'contact' && (
                <p className="message-text-body">{linkify(msg.message_text)}</p>
              )}
              {/* Reações do contato (👍❤️) — agrupadas por emoji */}
              {(() => {
                const reacoes = parsePayload(msg.reactions);
                const lista = reacoes ? Object.values(reacoes) : [];
                if (!lista.length) return null;
                const contagem = lista.reduce((acc, e) => ({ ...acc, [e]: (acc[e] || 0) + 1 }), {});
                return (
                  <div className="msg-reactions">
                    {Object.entries(contagem).map(([emoji, n]) => (
                      <span className="msg-reaction" key={emoji}>
                        {emoji}{n > 1 && <small>{n}</small>}
                      </span>
                    ))}
                  </div>
                );
              })()}
              <span className="message-time">
                {msg.edited_at && <span className="msg-edited">editada · </span>}
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {msg.sender_type === 'agent' && (
                  <span className={`msg-tick msg-tick-${msg.msg_status || 'sent'}`}>
                    {msg.msg_status === 'read' ? ' ✓✓' : msg.msg_status === 'delivered' ? ' ✓✓' : ' ✓'}
                  </span>
                )}
              </span>
            </div>
          </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
        {showScrollDown && (
          <button
            type="button"
            className="scroll-bottom-fab"
            onClick={() => scrollToBottom()}
            title="Ir para o final da conversa"
            aria-label="Ir para o final da conversa"
          >
            <ChevronDown size={22} />
          </button>
        )}
      </div>

      {/* ── Aviso de reatribuição ── */}
      {reassigned && (
        <div style={{
          background: '#FEF3C7', borderTop: '1px solid #F59E0B',
          padding: '10px 16px', fontSize: '13px', color: '#92400E',
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span>⚠️</span>
          <span>
            Esta conversa foi aceita por{' '}
            <strong>{users.find(u => String(u.id) === String(reassigned.agentId))?.name || 'outro agente'}</strong>.
            {' '}Apenas leitura disponível.
          </span>
        </div>
      )}

      {/* ── Quick reply dropdown ── */}
      {showQuickMenu && filteredQuickReplies.length > 0 && (
        <div className="quick-reply-dropdown">
          {filteredQuickReplies.slice(0, 6).map((q, idx) => (
            <div
              key={q.id}
              className={`quick-reply-item ${idx === qrHighlight ? 'is-highlighted' : ''}`}
              onMouseEnter={() => setQrHighlight(idx)}
              onClick={() => applyQuickReply(q)}
            >
              <strong>{q.shortcut}</strong>
              <span>
                {q.file_name && <Paperclip size={11} style={{verticalAlign:'middle', marginRight:4}}/>}
                {(q.message || q.file_name || '').substring(0, 80)}
              </span>
            </div>
          ))}
          <div className="quick-reply-hint">↑↓ navegar · Enter selecionar · Esc fechar</div>
        </div>
      )}

      {/* ── Quick replies panel ── */}
      {showQuickPanel && (
        <div className="quick-panel">
          <div className="quick-panel-header">
            <span>Respostas rápidas</span>
            <button className="quick-panel-close" onClick={() => setShowQuickPanel(false)}>×</button>
          </div>
          <div className="quick-panel-list">
            {quickReplies.length === 0 ? (
              <p className="quick-panel-empty">Nenhuma resposta rápida cadastrada.</p>
            ) : quickReplies.map((q, idx) => (
              <div
                key={q.id}
                className={`quick-panel-item ${idx === qrHighlight ? 'is-highlighted' : ''}`}
                onMouseEnter={() => setQrHighlight(idx)}
                onClick={() => { applyQuickReply(q); setShowQuickPanel(false); }}
              >
                <span className="quick-panel-shortcut">{q.shortcut}</span>
                <span className="quick-panel-msg">
                  {q.file_name && <Paperclip size={11} style={{verticalAlign:'middle', marginRight:4}}/>}
                  {(q.message || q.file_name || '').substring(0, 80)}
                </span>
              </div>
            ))}
            {quickReplies.length > 0 && (
              <div className="quick-reply-hint">↑↓ navegar · Enter selecionar · Esc fechar</div>
            )}
          </div>
        </div>
      )}

      {spellcheck && !spellcheck.checking && (
        <div className="spellcheck-overlay" onClick={() => setSpellcheck(null)}>
          <div className="spellcheck-modal" onClick={e => e.stopPropagation()}>
            <div className="spellcheck-header">
              <h3><SpellCheck size={16}/> Corretor ({spellcheck.matches.length} {spellcheck.matches.length === 1 ? 'sugestão' : 'sugestões'})</h3>
              <button onClick={() => setSpellcheck(null)}><X size={18}/></button>
            </div>
            <div className="spellcheck-body">
              {spellcheck.matches.map((m, i) => {
                const t = spellcheck.text;
                const ctxStart = Math.max(0, m.offset - 20);
                const ctxEnd = Math.min(t.length, m.offset + m.length + 20);
                const before = t.slice(ctxStart, m.offset);
                const wrong = t.slice(m.offset, m.offset + m.length);
                const after = t.slice(m.offset + m.length, ctxEnd);
                return (
                  <div key={i} className="spellcheck-item">
                    <div className="spellcheck-context">
                      {ctxStart > 0 && '…'}
                      {before}
                      <mark>{wrong}</mark>
                      {after}
                      {ctxEnd < t.length && '…'}
                    </div>
                    <div className="spellcheck-msg">{m.message}</div>
                    <div className="spellcheck-suggestions">
                      {m.replacements.length === 0 && (
                        <span className="spellcheck-no-sug">Sem sugestão automática</span>
                      )}
                      {m.replacements.map((s, j) => (
                        <button key={j} className="spellcheck-sug" onClick={() => applySuggestion(i, s)}>
                          {s}
                        </button>
                      ))}
                      <button className="spellcheck-ignore" onClick={() => ignoreSuggestion(i)}>
                        Ignorar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showStickerPanel && (
        <div className="quick-panel sticker-panel">
          <div className="quick-panel-header">
            <div className="picker-tabs">
              <button
                className={`picker-tab ${pickerTab === 'emoji' ? 'active' : ''}`}
                onClick={() => setPickerTab('emoji')}
              >Emojis</button>
              <button
                className={`picker-tab ${pickerTab === 'sticker' ? 'active' : ''}`}
                onClick={() => { setPickerTab('sticker'); if (stickers.length === 0) fetchStickers(); }}
              >Figurinhas</button>
            </div>
            <button className="quick-panel-close" onClick={() => setShowStickerPanel(false)}>×</button>
          </div>

          {pickerTab === 'emoji' && (
            <>
              <div className="emoji-categories">
                {EMOJI_CATEGORIES.map(c => (
                  <button
                    key={c.id}
                    className={`emoji-cat-btn ${emojiCategory === c.id ? 'active' : ''}`}
                    onClick={() => setEmojiCategory(c.id)}
                    title={c.name}
                  >{c.label}</button>
                ))}
              </div>
              <div className="emoji-grid">
                {(EMOJI_CATEGORIES.find(c => c.id === emojiCategory)?.emojis || []).map((e, i) => (
                  <button
                    key={`${emojiCategory}-${i}`}
                    className="emoji-cell"
                    onClick={() => insertEmoji(e)}
                  >{e}</button>
                ))}
              </div>
            </>
          )}

          {pickerTab === 'sticker' && (
            <>
              <div className="sticker-toolbar">
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{stickers.length} figurinha(s)</span>
                <button
                  className="sticker-upload-btn"
                  onClick={() => stickerInputRef.current?.click()}
                  title="Adicionar figurinha (.webp ou .gif)"
                  disabled={!!stickerUpload}
                >+ Adicionar</button>
              </div>
              <input
                type="file"
                ref={stickerInputRef}
                hidden
                accept="image/webp,image/gif,image/*"
                onChange={handleUploadSticker}
              />
              {stickerUpload && (
                <div className="sticker-upload-progress">
                  <div className="sticker-upload-info">
                    <span className="sticker-upload-name">📤 {stickerUpload.name}</span>
                    <span className="sticker-upload-percent">
                      {stickerUpload.processing
                        ? 'Convertendo gif...'
                        : `${stickerUpload.percent}%`}
                    </span>
                  </div>
                  <div className="sticker-upload-bar">
                    <div className="sticker-upload-fill" style={{ width: `${stickerUpload.percent}%` }}/>
                  </div>
                </div>
              )}
              <div className="sticker-grid">
                {stickers.length === 0 ? (
                  <p className="quick-panel-empty">
                    Nenhuma figurinha ainda. Clique em "+ Adicionar" pra subir uma .webp,
                    ou salve uma figurinha recebida no chat usando o botão 🔖.
                  </p>
                ) : stickers.map(s => (
                  <div key={s.id} className="sticker-item" onClick={() => handleSendSticker(s.id)} title="Enviar figurinha">
                    <img src={s.url} alt="figurinha" />
                    <button
                      className="sticker-delete"
                      onClick={(ev) => handleDeleteSticker(s.id, ev)}
                      title="Remover da biblioteca"
                    ><X size={11}/></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Barra "Respondendo a ..." acima do composer */}
      {replyingTo && (
        <div className="reply-bar">
          <CornerUpLeft size={16}/>
          <div className="reply-bar-content">
            <span className="reply-bar-sender">Respondendo a {replyingTo.senderName}</span>
            <span className="reply-bar-text">{replyingTo.text.substring(0, 100)}{replyingTo.text.length > 100 ? '…' : ''}</span>
          </div>
          <button className="reply-bar-cancel" onClick={() => setReplyingTo(null)}><X size={16}/></button>
        </div>
      )}

      {/* Preview do áudio gravado — ouvir antes de enviar */}
      {audioPreview && (
        <div className="audio-preview-bar">
          <audio src={audioPreview.url} controls className="audio-preview-player" />
          <span className="audio-preview-size">{audioPreview.sizeMB} MB</span>
          <button
            className="audio-preview-discard"
            onClick={handleDiscardAudio}
            title="Descartar"
            disabled={loading}
          >
            <Trash2 size={14}/> Descartar
          </button>
          <button
            className="audio-preview-send"
            onClick={handleSendAudioPreview}
            title="Enviar áudio"
            disabled={loading}
          >
            <Send size={14}/> Enviar
          </button>
        </div>
      )}

      {/* Barra de progresso de upload */}
      {uploadProgress && (
        <div className="upload-progress">
          <div className="upload-progress-info">
            <span className="upload-progress-name">📤 Enviando {uploadProgress.name}</span>
            <span className="upload-progress-percent">
              {uploadProgress.percent < 100 ? `${uploadProgress.percent}%` : 'Processando...'}
              {uploadProgress.sizeMB && ` · ${uploadProgress.sizeMB} MB`}
            </span>
          </div>
          <div className="upload-progress-bar">
            <div className="upload-progress-fill" style={{ width: `${uploadProgress.percent}%` }} />
          </div>
        </div>
      )}

      {/* ── Composer ── */}
      <form onSubmit={handleSendMessage} className="message-form">
        <input type="file" ref={fileInputRef} hidden multiple onChange={handleUploadMedia}
          accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.xml,.json,.zip,.rar,.7z,.tar,.gz" />
        <button type="button" className="attach-btn" disabled={!!reassigned}
          onClick={() => fileInputRef.current?.click()} title="Anexar mídia">
          <Paperclip size={18}/>
        </button>
        <button type="button" className="attach-btn" disabled={!!reassigned}
          onClick={() => setShowContactPicker(true)} title="Enviar contato">
          <UserSquare size={18}/>
        </button>
        <button type="button" className="attach-btn" disabled={!!reassigned}
          onClick={() => setShowQuickPanel(v => !v)} title="Respostas rápidas">
          <Zap size={18}/>
        </button>
        <button type="button" className="attach-btn" disabled={!!reassigned}
          onClick={() => setShowStickerPanel(v => !v)}
          title="Emojis e figurinhas">
          <Smile size={18}/>
        </button>
        <button type="button"
          className={`attach-btn ${autoSpellcheck ? 'autocorrect-on' : ''}`}
          disabled={!!reassigned || !!spellcheck?.checking}
          onClick={(e) => {
            // Click esquerdo simples: revisa o texto atual (manual)
            // Click direito ou Shift+click: alterna auto-correção no envio
            if (e.shiftKey) { toggleAutoSpellcheck(); return; }
            if (newMessage.trim()) handleSpellcheck();
            else toggleAutoSpellcheck();
          }}
          onContextMenu={(e) => { e.preventDefault(); toggleAutoSpellcheck(); }}
          title={`Corretor — auto-correção ao enviar: ${autoSpellcheck ? 'LIGADA' : 'DESLIGADA'}\nClique no texto pra revisar agora · Shift+click ou botão direito pra alternar`}>
          <SpellCheck size={18}/>
        </button>
        <button type="button"
          className={`attach-btn${isRecording ? ' recording-btn' : ''}`}
          disabled={!!reassigned || loading}
          onClick={isRecording ? handleStopRecording : handleStartRecording}
          title={isRecording ? 'Parar gravação' : 'Gravar áudio'}>
          {isRecording ? <Square size={18}/> : <Mic size={18}/>}
        </button>
        <textarea
          ref={textareaRef}
          rows={1}
          value={newMessage}
          onChange={(e) => { handleMessageChange(e.target.value); autoGrowTextarea(e.target); }}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            // Dropdown de respostas rápidas aberto — navega com ↑↓, escolhe com Enter, fecha com Esc
            if (showQuickMenu && filteredQuickReplies.length > 0) {
              const list = filteredQuickReplies.slice(0, 6);
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setQrHighlight(i => (i + 1) % list.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setQrHighlight(i => (i - 1 + list.length) % list.length);
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                const q = list[qrHighlight] || list[0];
                if (q) applyQuickReply(q);
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setShowQuickMenu(false);
                return;
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                const q = list[qrHighlight] || list[0];
                if (q) applyQuickReply(q);
                return;
              }
            }
            if (e.key === 'Enter' && !e.shiftKey && !isTouchDevice) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
          placeholder={reassigned ? 'Conversa transferida para outro agente.' : 'Digite sua mensagem ou / para respostas rápidas...'}
          disabled={loading || !!reassigned}
          spellCheck={true}
          lang="pt-BR"
          autoCorrect="on"
          autoCapitalize="sentences"
        />
        <button type="submit" className="send-btn" disabled={loading || !newMessage.trim() || !!reassigned}>
          <Send size={17}/>
        </button>
      </form>

      {/* ── Barra de seleção flutuante ── */}
      {selectMode && (
        <div className="select-bar">
          <span className="select-bar-count">{selectedMsgIds.size} selecionada(s)</span>
          <button className="select-bar-btn"
            disabled={selectedMsgIds.size === 0}
            onClick={openForwardModal}>
            <Forward size={16}/> Encaminhar
          </button>
          {selectedMsgIds.size === 1 && (() => {
            const id = [...selectedMsgIds][0];
            const m = messages.find(x => x.id === id);
            if (!m) return null;
            return (
              <button className="select-bar-btn" onClick={() => {
                setReplyingTo({
                  id: m.id,
                  text: (m.message_text || '').replace(/^\*[^*]+\*\s*\n?/, '') || `[${m.media_type || 'mídia'}]`,
                  senderName: m.sender_type === 'agent'
                    ? (m.sender_name || 'Você')
                    : (nameOverride || conversation.contact_name || 'Cliente'),
                  senderType: m.sender_type
                });
                exitSelectMode();
                setTimeout(() => textareaRef.current?.focus(), 50);
              }}>
                <CornerUpLeft size={16}/> Responder
              </button>
            );
          })()}
          {editableMessage && (
            <button className="select-bar-btn" onClick={openEdit}>
              <Pencil size={16}/> Editar
            </button>
          )}
          <button className="select-bar-btn select-bar-btn-danger"
            disabled={selectedMsgIds.size === 0}
            onClick={() => setShowDeleteModal(true)}>
            <Trash2 size={16}/> Apagar
          </button>
          <button className="select-bar-cancel" onClick={exitSelectMode}>
            <X size={16}/>
          </button>
        </div>
      )}

      {editModal && (
        <div className="lightbox-overlay" onClick={() => setEditModal(null)}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-header">Editar mensagem</div>
            <div className="delete-modal-body">A nova versão substitui a original no WhatsApp do destinatário também.</div>
            <textarea
              autoFocus
              value={editModal.text}
              onChange={e => setEditModal({ ...editModal, text: e.target.value })}
              spellCheck={true}
              lang="pt-BR"
              autoCorrect="on"
              autoCapitalize="sentences"
              style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 6, border: '1px solid var(--border)', fontFamily: 'inherit', fontSize: 14, marginBottom: 12, resize: 'vertical' }}
            />
            <div className="delete-modal-actions">
              <button className="delete-modal-btn" onClick={submitEdit}>Salvar</button>
              <button className="delete-modal-btn delete-modal-btn-cancel" onClick={() => setEditModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="lightbox-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-header">Apagar {selectedMsgIds.size} mensagem(ns)?</div>
            <div className="delete-modal-body">
              Escolha se quer remover apenas da sua visualização ou também no WhatsApp do destinatário
              (só funciona para mensagens enviadas por você).
            </div>
            <div className="delete-modal-actions">
              <button className="delete-modal-btn" onClick={() => doDelete(false)}>
                Apagar para mim
              </button>
              {hasOwnSelected && (
                <button className="delete-modal-btn delete-modal-btn-danger" onClick={() => doDelete(true)}>
                  Apagar para todos
                </button>
              )}
              <button className="delete-modal-btn delete-modal-btn-cancel" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de encaminhamento ── */}
      {showForwardModal && (
        <div className="lightbox-overlay" onClick={() => setShowForwardModal(false)}>
          <div className="forward-modal" onClick={e => e.stopPropagation()}>
            <div className="forward-modal-header">
              <span>Encaminhar para...</span>
              <button onClick={() => setShowForwardModal(false)}><X size={18}/></button>
            </div>
            <input
              className="forward-search"
              placeholder="Buscar conversa ou número..."
              value={forwardSearch}
              onChange={e => setForwardSearch(e.target.value)}
              autoFocus
            />
            <div className="forward-list">
              {forwardLoading && <p className="forward-empty">Carregando...</p>}
              {!forwardLoading && forwardConvs
                .filter(c =>
                  (c.contact_name || '').toLowerCase().includes(forwardSearch.toLowerCase()) ||
                  (c.contact_number || '').includes(forwardSearch)
                )
                .map(c => (
                  <div key={c.id} className="forward-item" onClick={() => doForward(c.id)}>
                    <strong>{c.contact_name || 'Sem nome'}</strong>
                    <span>{c.contact_number}</span>
                  </div>
                ))
              }
              {!forwardLoading && forwardConvs.length === 0 && (
                <p className="forward-empty">Nenhuma conversa aberta além desta.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modais ── */}
      {confirmResolve && (
        <ConfirmModal
          title="Encerrar atendimento"
          message="Tem certeza que deseja encerrar esta conversa? O cliente não poderá mais enviar mensagens por este ticket."
          confirmLabel="Encerrar"
          onConfirm={doResolve}
          onCancel={() => setConfirmResolve(false)}
          danger
        />
      )}

      {showTransferModal && (
        <TransferModal
          departments={departments}
          users={users}
          currentDeptId={conversation.department_id}
          onlineUserIds={onlineUserIds}
          onConfirm={doTransfer}
          onCancel={() => setShowTransferModal(false)}
        />
      )}

      {showContactPicker && (
        <ContactPickerModal
          onConfirm={handleSendContact}
          onCancel={() => setShowContactPicker(false)}
        />
      )}

      {lightboxUrl && (
        <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
          <div className="lightbox-box" onClick={e => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Visualizar" className="lightbox-img" />
            <div className="lightbox-actions">
              <button className="lightbox-btn lightbox-btn-close" onClick={() => setLightboxUrl(null)}>✕ Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Menu de contexto por mensagem (botão direito / chevron de hover) */}
      {msgMenu && (
        <div
          className="msg-menu"
          style={{ left: msgMenu.x, top: msgMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {!reassigned && (
            <button className="msg-menu-item" onClick={() => replyToMsg(msgMenu.msg)}>
              <CornerUpLeft size={14}/> Responder
            </button>
          )}
          {!!msgMenu.msg.message_text && (
            <button className="msg-menu-item" onClick={() => copyMsgText(msgMenu.msg)}>
              <Copy size={14}/> Copiar texto
            </button>
          )}
          <button className="msg-menu-item" onClick={() => forwardMsgSingle(msgMenu.msg)}>
            <Forward size={14}/> Encaminhar
          </button>
          {isMsgEditable(msgMenu.msg) && (
            <button className="msg-menu-item" onClick={() => editMsg(msgMenu.msg)}>
              <Pencil size={14}/> Editar
            </button>
          )}
          {canDelete(msgMenu.msg) && (
            <button className="msg-menu-item danger" onClick={() => deleteMsgSingle(msgMenu.msg)}>
              <Trash2 size={14}/> Apagar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ConversationDetail;
